// ─── Suppliers Page ──────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supplierAPI, settingsAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import toast from 'react-hot-toast';
import SearchableSelect from '../components/ui/SearchableSelect';
import PublishItemModal from '../components/website/PublishItemModal';
import { Plus, Truck, Edit2, X, Save, Trash2, PlusCircle, AlertCircle, RefreshCw, Loader2, BookOpen, Search, Calendar, Maximize2, ZoomIn, Download, CreditCard, Building2, Globe, ChevronLeft, ChevronRight } from 'lucide-react';

const EMPTY_SUP = { supplierName: '', mobile: '', email: '', address: '', city: '', state: '', gstNumber: '', panNumber: '', bankName: '', accountNo: '', ifscCode: '', openingBalance: 0, creditDays: 0 };

export function SuppliersPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_SUP);
  const [editID, setEditID] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await supplierAPI.getAll(); setData(r.data.data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY_SUP); setEditID(null); setModal(true); };
  const openEdit = (r) => {
    setForm({ supplierName: r.supplierName, mobile: r.mobile || '', email: r.email || '', address: r.address || '', city: r.city || '', state: r.state || '', gstNumber: r.gstNumber || '', panNumber: r.panNumber || '', bankName: r.bankName || '', accountNo: r.accountNo || '', ifscCode: r.ifscCode || '', openingBalance: r.openingBalance || 0, creditDays: r.creditDays || 0 });
    setEditID(r.supplierID); setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editID) { await supplierAPI.update(editID, { ...form, isActive: true }); toast.success('Updated'); }
      else { await supplierAPI.create(form); toast.success('Created'); }
      setModal(false); load();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const inp = (k) => ({ value: form[k], onChange: e => setForm({ ...form, [k]: e.target.value }) });

  // ── Supplier Ledger ─────────────────────────────────────
  const dateStr = (d) => d.toISOString().split('T')[0];
  const defaultFrom = () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return dateStr(d); };
  const [ledgerModal, setLedgerModal] = useState(null); // supplier row
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerRange, setLedgerRange] = useState({ fromDate: defaultFrom(), toDate: dateStr(new Date()) });

  const fetchLedger = async (supplierID, range) => {
    setLedgerLoading(true);
    try {
      const res = await supplierAPI.getLedger(supplierID, range);
      setLedgerRows(res.data.data || []);
    } catch { toast.error('Failed to load ledger'); setLedgerRows([]); }
    finally { setLedgerLoading(false); }
  };

  const openLedger = (row) => {
    const range = { fromDate: defaultFrom(), toDate: dateStr(new Date()) };
    setLedgerRange(range);
    setLedgerModal(row);
    fetchLedger(row.supplierID, range);
  };

  // Running balance: Credit (purchases — we owe) minus Debit (payments)
  let running = 0;
  const ledgerWithBalance = ledgerRows.map(r => {
    running += Number(r.credit || 0) - Number(r.debit || 0);
    return { ...r, balance: running };
  });
  const totDebit = ledgerRows.reduce((s, r) => s + Number(r.debit || 0), 0);
  const totCredit = ledgerRows.reduce((s, r) => s + Number(r.credit || 0), 0);
  const fmtL = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const exportLedgerExcel = async () => {
    if (!ledgerWithBalance || ledgerWithBalance.length === 0) {
      toast.error('No ledger data to export');
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const rows = ledgerWithBalance.map(r => ({
        'Type': r.transType,
        'Date': new Date(r.transDate).toLocaleDateString('en-IN'),
        'Ref No': r.refNo || '—',
        'Debit (Paid)': Number(r.debit || 0),
        'Credit (Owed)': Number(r.credit || 0),
        'Balance': Number(r.balance || 0),
      }));

      rows.push({
        'Type': 'TOTALS',
        'Date': '',
        'Ref No': '',
        'Debit (Paid)': totDebit,
        'Credit (Owed)': totCredit,
        'Balance': totCredit - totDebit,
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Ledger');
      const filename = `Supplier_Ledger_${(ledgerModal?.supplierName || 'Supplier').replace(/[^a-zA-Z0-9]/g, '_')}_${ledgerRange.fromDate}_to_${ledgerRange.toDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success('Excel exported successfully');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Are you sure you want to delete supplier "${r.supplierName}"?`)) return;
    try {
      await supplierAPI.delete(r.supplierID);
      toast.success('Supplier deleted');
      load();
    } catch {
      toast.error('Failed to delete supplier');
    }
  };

  const columns = [
    { header: 'Code', key: 'supplierCode', render: r => <code style={{ fontSize: 12 }}>{r.supplierCode}</code> },
    { header: 'Name', key: 'supplierName', render: r => <span style={{ fontWeight: 600 }}>{r.supplierName}</span> },
    { header: 'Mobile', key: 'mobile' },
    { header: 'City', key: 'city' },
    { header: 'GST No.', key: 'gstNumber', render: r => r.gstNumber || <span className="text-muted">—</span> },
    { header: 'Credit Days', key: 'creditDays', align: 'right' },
    {
      header: 'Actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button className="btn btn-ghost btn-sm btn-icon" title="View Ledger"
            style={{ color: 'var(--navy-600)' }} onClick={() => openLedger(r)}>
            <BookOpen size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => openEdit(r)}><Edit2 size={14} /></button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Delete" style={{ color: 'var(--red)' }} onClick={() => handleDelete(r)}><Trash2 size={14} /></button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="flex-between mb-6">
        <div><h1>Suppliers</h1><p className="text-muted">{data.length} suppliers</p></div>
        <button className="btn btn-amber" onClick={openAdd}><Plus size={16} /> Add Supplier</button>
      </div>
      <div className="card">
        <div style={{ padding: '16px 20px' }}>
          <DataTable data={data} columns={columns} loading={loading} searchKeys={['supplierName', 'supplierCode', 'mobile', 'city']} emptyIcon={<Truck size={36} />} emptyText="No suppliers yet" />
        </div>
      </div>
      {/* ── Supplier Ledger Modal ── */}
      {ledgerModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setLedgerModal(null)}>
          <div className="modal" style={{ maxWidth: 780 }}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--silver-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOpen size={18} color="var(--navy-700)" />
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>Supplier Ledger</h3>
                  <p style={{ fontSize: 12, color: 'var(--silver-500)', margin: 0 }}>
                    {ledgerModal.supplierCode} · {ledgerModal.supplierName}
                  </p>
                </div>
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setLedgerModal(null)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ paddingTop: 14 }}>
              {/* Date range */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: 11 }}>From</label>
                  <input className="form-control" type="date" value={ledgerRange.fromDate}
                    onChange={e => setLedgerRange({ ...ledgerRange, fromDate: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: 11 }}>To</label>
                  <input className="form-control" type="date" value={ledgerRange.toDate}
                    onChange={e => setLedgerRange({ ...ledgerRange, toDate: e.target.value })} />
                </div>
                <button className="btn btn-primary btn-sm" style={{ height: 38 }}
                  onClick={() => fetchLedger(ledgerModal.supplierID, ledgerRange)}>
                  <RefreshCw size={13} /> Load
                </button>
                <button className="btn btn-sm" style={{ height: 38, background: '#10b981', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, padding: '0 14px', borderRadius: 6, opacity: ledgerWithBalance.length === 0 ? 0.6 : 1, cursor: 'pointer' }}
                  onClick={exportLedgerExcel} disabled={ledgerWithBalance.length === 0}>
                  <Download size={14} /> Export Excel
                </button>
              </div>

              {/* Ledger table */}
              {ledgerLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid var(--silver-200)', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--silver-100)' }}>
                        {['Type', 'Date', 'Ref No', 'Debit (Paid)', 'Credit (Owed)', 'Balance'].map((h, hi) => (
                          <th key={h} style={{ padding: '9px 12px', textAlign: hi >= 3 ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--silver-500)', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerWithBalance.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: 28, textAlign: 'center', color: 'var(--silver-400)' }}>No transactions in this period</td></tr>
                      ) : ledgerWithBalance.map((r, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid var(--silver-100)' }}>
                          <td style={{ padding: '8px 12px' }}>
                            <span className={`badge ${r.transType === 'PURCHASE' ? 'badge-navy' : r.transType === 'PAYMENT' ? 'badge-green' : r.transType === 'PAYMENT REVERSAL' ? 'badge-amber' : 'badge-blue'}`} style={{ fontSize: 10.5 }}>
                              {r.transType}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{new Date(r.transDate).toLocaleDateString('en-IN')}</td>
                          <td style={{ padding: '8px 12px' }}><code style={{ fontSize: 12 }}>{r.refNo || '—'}</code></td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--mono)', color: r.debit > 0 ? 'var(--green)' : 'var(--silver-400)' }}>{r.debit > 0 ? `₹${fmtL(r.debit)}` : '—'}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--mono)', color: r.credit > 0 ? 'var(--red)' : 'var(--silver-400)' }}>{r.credit > 0 ? `₹${fmtL(r.credit)}` : '—'}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: r.balance > 0 ? 'var(--red)' : 'var(--navy-800)' }}>₹{fmtL(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {ledgerWithBalance.length > 0 && (
                      <tfoot>
                        <tr style={{ borderTop: '2px solid var(--silver-200)', background: 'var(--silver-100)' }}>
                          <td colSpan={3} style={{ padding: '9px 12px', fontWeight: 700 }}>Totals</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--green)' }}>₹{fmtL(totDebit)}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--red)' }}>₹{fmtL(totCredit)}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 800, color: totCredit - totDebit > 0 ? 'var(--red)' : 'var(--navy-800)' }}>₹{fmtL(totCredit - totDebit)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
              <p style={{ fontSize: 11.5, color: 'var(--silver-500)', marginTop: 10, marginBottom: 0 }}>
                💡 Credit = purchases (amount you owe) · Debit = payments made · Balance = pending payable
              </p>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header"><h3>{editID ? 'Edit Supplier' : 'Add Supplier'}</h3><button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}><X size={18} /></button></div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="grid-2" style={{ gap: 12 }}>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Supplier Name *</label><input className="form-control" required {...inp('supplierName')} /></div>
                  <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" {...inp('mobile')} /></div>
                  <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" {...inp('email')} /></div>
                  <div className="form-group"><label className="form-label">GST Number</label><input className="form-control" {...inp('gstNumber')} /></div>
                  <div className="form-group"><label className="form-label">PAN Number</label><input className="form-control" {...inp('panNumber')} /></div>
                  <div className="form-group"><label className="form-label">City</label><input className="form-control" {...inp('city')} /></div>
                  <div className="form-group"><label className="form-label">State</label><input className="form-control" {...inp('state')} /></div>
                  <div className="form-group"><label className="form-label">Bank Name</label><input className="form-control" {...inp('bankName')} /></div>
                  <div className="form-group"><label className="form-label">Account No.</label><input className="form-control" {...inp('accountNo')} /></div>
                  <div className="form-group"><label className="form-label">IFSC Code</label><input className="form-control" {...inp('ifscCode')} /></div>
                  <div className="form-group"><label className="form-label">Credit Days</label><input className="form-control" type="number" {...inp('creditDays')} /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />}{editID ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// PURCHASE PAGE — Full Excel Template Fields
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
import { purchaseAPI, supplierAPI as supAPI, itemAPI as itmAPI, masterAPI as branchMAPI, skuAPI } from '../services/api';
import { ShoppingCart, Camera, XCircle, Printer, IndianRupee, RotateCcw, ChevronDown, Eye } from 'lucide-react';
import QRCode from 'qrcode';

// â”€â”€ Jewellery tag sticker — full content, proven layout â”€â”€â”€â”€â”€â”€â”€â”€
// Logo + QR + SKU + net weight + full purity/metal name, laid out
// left-to-right at 50mm wide. Printed and confirmed working: fits
// cleanly on the physical die-cut with no clipping.
// Build one tag's inner HTML (logo + QR + details image) for a row, plus its qty.
const buildTagContent = async (row) => {
  const sku = row.skuNo || row.itemCode || '';
  const wt = Number(row.weight || 0).toFixed(3);
  const qty = Math.max(1, Math.min(50, Math.round(Number(row.quantity) || 1)));
  const purity = row.purityCode || '925';
  const metal = `${purity} STERLING SILVER`;
  // QR holds the public product-page URL (like GCAL cert tags): scanning
  // opens https://<domain>/t/<sku> — the public TagViewPage with the item
  // photo, SKU, details, quantity, MRP and sale price. Works from any
  // network since the app is hosted on the public domain (the old LAN-IP
  // link only resolved on the shop network, which is why plain text was
  // used before).
  //
  // Kept as short as possible (short /t/ alias, no query params) — more
  // characters means more QR modules squeezed into the same tiny print
  // box, and the printer can't resolve modules that small.
  const tagText = `${window.location.origin}/t/${encodeURIComponent(sku)}`;
  // Raster PNG QR, not vector SVG — this print goes through window.print() â†’
  // the Windows driver's GDI rasterizer, with no raw-command bypass like the
  // WinForms label app has. That rasterizer has to individually thin/threshold
  // every one of the QR's many adjacent thin SVG shapes, which corrupts the
  // module grid into a moirÃ©/garbled mess at print size. A single pre-rendered
  // high-res bitmap survives that pipeline as a plain image scale instead.
  let qrDataUrl = '';
  try {
    qrDataUrl = await QRCode.toDataURL(tagText, {
      type: 'image/png', margin: 0, errorCorrectionLevel: 'L', width: 480,
      color: { dark: '#000000', light: '#ffffff' },
    });
  } catch { }

  const logoImg = `<img src="/cq-mark-crisp.png" alt="CQ" />`;

  // ── Details text as a high-DPI solid-black image ──────────────
  // Small text rendered as live HTML prints LIGHT on the thermal head:
  // its anti-aliased edges become grey pixels the printer won't fire.
  // Pre-rendering the three lines onto a high-resolution canvas (solid
  // black) makes them print dark and crisp, exactly like the QR/logo.
  let detailsUrl = '', detailsW = 0, detailsH = 0;
  try {
    const DPMM = 48;                                  // ~1200 DPI render
    const ptPx = (pt) => pt * 0.35278 * DPMM;         // pt → device px
    const lines = [
      { text: sku, px: ptPx(6.5) },
      { text: `Wt: ${wt} g`, px: ptPx(6.5) },
      { text: metal, px: ptPx(5.0) },
    ];
    const gap = 0.55 * DPMM, pad = 0.3 * DPMM;
    const font = (px) => `600 ${px}px Arial, sans-serif`;
    const c = document.createElement('canvas');
    let ctx = c.getContext('2d');
    let maxW = 0;
    lines.forEach(l => { ctx.font = font(l.px); maxW = Math.max(maxW, ctx.measureText(l.text).width); });
    c.width = Math.ceil(maxW + pad * 2);
    c.height = Math.ceil(pad * 2 + lines.reduce((s, l) => s + l.px, 0) + gap * (lines.length - 1));
    ctx = c.getContext('2d');                          // re-fetch (resize resets state)
    ctx.fillStyle = '#000';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    let y = pad;
    lines.forEach(l => { ctx.font = font(l.px); ctx.fillText(l.text, pad, y); y += l.px + gap; });
    detailsUrl = c.toDataURL('image/png');
    detailsW = c.width / DPMM;
    detailsH = c.height / DPMM;
  } catch { }

  const detailsBlock = detailsUrl
    ? `<div class="details"><img class="detailsImg" src="${detailsUrl}" style="width:${detailsW.toFixed(2)}mm;height:${detailsH.toFixed(2)}mm;" /></div>`
    : `<div class="details">
        <div class="skuBig">${sku}</div>
        <div class="line">Wt: ${wt} g</div>
        <div class="line metal">${metal}</div>
      </div>`;

  const tag = `
    <div class="tag">
      <div class="logo">
        <div class="cqMark">${logoImg}</div>
      </div>
      ${qrDataUrl ? `<div class="qr"><img src="${qrDataUrl}" alt="QR" /></div>` : ''}
      <div class="divider"></div>
      ${detailsBlock}
    </div>`;

  return { tag, qty, sku };
};

// Shared print-window CSS for jewellery tag stickers
const TAG_PRINT_CSS = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; background: #9ca3af; padding: 16px; }

      /* Content sits at the left of the physical label, well clear of
         the tag's right edge — this size printed cleanly with no
         clipping on "STERLING SILVER". */
      .tag {
        width: 54mm; height: 12mm; background: #fff; border-radius: 1mm;
        display: flex; align-items: center;
        overflow: hidden; color: #000;
        padding: 0 1mm;
        margin-left: 0mm;
      }
      .logo   { width: 9.5mm; height: 9.5mm; position: relative; display: flex; align-items: center; justify-content: center; line-height: 1; flex-shrink: 0; margin-left: 2mm; }
      /* cq-mark-crisp.png is a tightly-cropped, high-resolution (672Ã—363)
         export of just the CQ mark. It fills the 9.5mm box width and is
         vertically centred. Smooth (not pixelated) rendering — the source
         is high-DPI, so anti-aliased scaling prints crisp instead of the
         jagged/blurry result nearest-neighbour gave with the old low-res PNG. */
      .cqMark { width: 9.5mm; height: 9.5mm; display: flex; align-items: center; justify-content: center; }
      .cqMark img { width: 100%; height: auto; display: block; image-rendering: auto; }
      .qr     { width: 8.5mm; height: 8.5mm; flex-shrink: 0; margin-left: 4.5mm; }
      .qr img {
        width: 100%; height: 100%; display: block;
        image-rendering: pixelated; image-rendering: -moz-crisp-edges; image-rendering: crisp-edges;
      }
      .divider { width: .5mm; height: 10mm; border-left: .5mm dashed #000; margin-left: 2.5mm; margin-right: 1.5mm; }
      .details { display: flex; flex-direction: column; align-items: flex-start; justify-content: center; gap: .3mm; padding-left: 0.5mm; margin-left: 0mm; min-width: max-content; flex-shrink: 0; transform: rotate(180deg); }
      /* Small thermal-label text: medium weight (500) keeps strokes solid
         without the heavy look of bold, and geometricPrecision + crisp
         font-smoothing render sharper edges through the GDI print path. */
      .skuBig { font-size: 6.5pt; font-weight: 500; letter-spacing: .1px; white-space: nowrap; color: #000; text-rendering: geometricPrecision; -webkit-font-smoothing: antialiased; }
      .line   { font-size: 6.5pt; font-weight: 500; letter-spacing: .1px; white-space: nowrap; color: #000; text-rendering: geometricPrecision; -webkit-font-smoothing: antialiased; }
      .line.metal { font-size: 5pt; font-weight: 500; letter-spacing: .1px; white-space: nowrap; color: #000; text-rendering: geometricPrecision; -webkit-font-smoothing: antialiased; }
      /* Pre-rendered high-DPI text image — prints solid black, no anti-alias greys */
      .detailsImg { display: block; }

      .toolbar { text-align: center; margin-bottom: 12px; }
      .toolbar button { padding: 8px 22px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; border-radius: 6px; background: #0f172a; color: #fff; }
      .hint { text-align: center; font-size: 11px; color: #f3f4f6; margin-bottom: 10px; }

      /* Page = the printer driver's own stock profile ("81-12", confirmed in
         its Printing Preferences dialog): 81mm × 12mm. The driver's label
         database already handles the inter-label gap on its own, so no
         extra gap is added into the page height here — doing that too (the
         old 13.5mm) meant two independent things were both trying to
         account for the same gap. */
      .page { width: 81mm; height: 12mm; margin: 0 auto 4mm; background: rgba(255,255,255,.5); display: flex; align-items: flex-start; }

      @media print {
        body { background: #fff; padding: 0; }
        .toolbar, .hint { display: none; }
        .page { margin: 0; page-break-after: always; background: none; }
        @page { size: 81mm 12mm; margin: 0; }
      }
`;

// Open a print window containing the given tag pages
const writeTagWindow = (title, pagesHtml, count) => {
  const w = window.open('', '_blank', 'width=920,height=620');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <style>${TAG_PRINT_CSS}</style></head><body>
    <div class="toolbar"><button onclick="window.print()">&#128424;&#65039; Print ${count} tag${count > 1 ? 's' : ''}</button></div>
    <div class="hint">Printable area: 81 &times; 12 mm (matches the printer driver's own "81-12" stock &mdash; it handles the label gap) &middot; content sits left, rest of the width is blank stock</div>
    ${pagesHtml}
  </body></html>`);
  w.document.close();
};

// Print a single item's tag (qty copies)
const printItemTag = async (row) => {
  const { tag, qty, sku } = await buildTagContent(row);
  const pages = Array(qty).fill(`<div class="page">${tag}</div>`).join('');
  writeTagWindow(`Tag ${sku}`, pages, qty);
};

// Print tags for every line of an invoice (each line × its qty) in one window
const printInvoiceTags = async (rows) => {
  const pages = [];
  let total = 0;
  for (const row of (rows || [])) {
    const { tag, qty } = await buildTagContent(row);
    for (let k = 0; k < qty; k++) pages.push(`<div class="page">${tag}</div>`);
    total += qty;
  }
  if (!total) return;
  writeTagWindow('Invoice Tags', pages.join(''), total);
};

// â”€â”€ Printer calibration strip â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Uses the exact same per-page size as the real tag print (100mm Ã—
// 13.3mm — proven to work: 2 tags produced exactly "2 sheets", no
// unwanted splitting). A single tall custom page doesn't survive the
// print pipeline the same way, so instead this prints N consecutive
// numbered labels, each with a 0—12mm ruler along the left edge.
// If the pitch is correct, mark "0" lands at the same spot on every
// physical label. If it drifts, the mm amount it has crept by Ã· label
// number = the exact correction needed.
const printCalibrationStrip = () => {
  // Matches the printer driver's own "81-12" stock profile (see printItemTag) —
  // the driver handles the inter-label gap itself, so this doesn't add one.
  const pageW = 81, pageH = 12, count = 6;
  let ticks = '';
  for (let mm = 0; mm <= 12; mm++) {
    const major = mm % 5 === 0;
    ticks += `
      <div class="tick ${major ? 'major' : ''}" style="top:${mm}mm">
        <div class="tickLine"></div>
        ${major ? `<div class="tickLabel">${mm}</div>` : ''}
      </div>`;
  }
  const page = (n) => `
    <div class="page">
      <div class="ruler">${ticks}</div>
      <div class="pageNo">LABEL ${n}</div>
    </div>`;

  const w = window.open('', '_blank', 'width=920,height=700');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>Calibration Strip</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; background: #9ca3af; padding: 16px; }
      .toolbar { text-align: center; margin-bottom: 12px; }
      .toolbar button { padding: 8px 22px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; border-radius: 6px; background: #0f172a; color: #fff; }
      .hint { text-align: center; font-size: 12px; color: #111; margin-bottom: 14px; max-width: 520px; margin-left: auto; margin-right: auto; line-height: 1.5; background: #fff; padding: 10px 14px; border-radius: 8px; }

      .page { position: relative; width: ${pageW}mm; height: ${pageH}mm; background: #fff; margin: 0 auto 4mm; overflow: hidden; }
      .ruler { position: relative; width: 20mm; height: 12mm; margin-top: .3mm; }
      .tick { position: absolute; left: 0; width: 100%; }
      .tickLine { width: 4mm; height: .25mm; background: #000; }
      .tick.major .tickLine { width: 8mm; height: .4mm; }
      .tickLabel { position: absolute; left: 9mm; top: -1.6mm; font-size: 6.5pt; font-weight: 700; font-family: 'Courier New', monospace; }
      .pageNo { position: absolute; left: 25mm; top: 3mm; font-size: 10pt; font-weight: 900; letter-spacing: .05em; }

      @media print {
        body { background: #fff; padding: 0; }
        .toolbar, .hint { display: none; }
        .page { margin: 0; page-break-after: always; }
        @page { size: ${pageW}mm ${pageH}mm; margin: 0; }
      }
    </style></head><body>
    <div class="toolbar"><button onclick="window.print()">ðŸ–¨ï¸ Print Calibration Strip (${count} labels)</button></div>
    <div class="hint">
      <b>How to use:</b> print at 100% scale (no "fit to page"), same as a real tag.
      Look at the ruler's <b>0mm mark</b> on every physical label — if it always lands
      at the same spot relative to the die-cut edge, the current size (${pageW}Ã—${pageH}mm) is correct.
      If it creeps down/up by label 6, tell me how many mm it moved — that Ã· 5 is the
      per-label correction needed.
    </div>
    ${Array.from({ length: count }, (_, i) => page(i + 1)).join('')}
  </body></html>`);
  w.document.close();
};

const todayStr = () => new Date().toISOString().split('T')[0];

const EMPTY_LINE = {
  itemID: '', vendorSRNo: '',
  // Item extra fields (auto-filled from item master)
  skuNo: '', collection: '', productSize: '', description: '', isManualSKU: false,
  quantity: 1,
  grossWeight: '', weight: '',
  labourPerGram: '', silverRatePerGram: '',
  rate: '',
  cogs: '', mrp: '',
  // Pink fields
  setComboPrice: '',
  discount: 0,
  salePrice: '',
  fixedExpenses: 0,
  otherExpenses: 0,
  productStatus: 'IN STOCK',
  gstPercent: 3, isIGST: false,
  // Image
  imageFile: null, imagePreview: null, imageBase64: null,
};

// â”€â”€ Base64 converter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ── Lazy item-view image ──────────────────────────────────────
const detailImageCache = new Map();

function LazyDetailImage({ detailId, skuNo, itemName, onBig }) {
  const [img, setImg] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!detailId) return;
    if (detailImageCache.has(detailId)) {
      setImg(detailImageCache.get(detailId));
      return;
    }
    let cancelled = false;
    setLoading(true);
    purchaseAPI.getDetailImage(detailId)
      .then(res => {
        if (cancelled) return;
        const raw = res.data?.data?.imageData || res.data?.data?.imagePath || res.data?.data?.ImagePreview || null;
        let finalUrl = null;
        if (raw) {
          if (raw.startsWith('http') || raw.startsWith('data:')) {
            finalUrl = raw;
          } else if (raw.startsWith('/') || raw.includes('/uploads/') || /\.(png|jpg|jpeg|webp|gif)$/i.test(raw)) {
            const apiHost = process.env.REACT_APP_API_URL || 'http://localhost:5002';
            finalUrl = `${apiHost}${raw.startsWith('/') ? '' : '/'}${raw}`;
          } else if (raw.length > 30) {
            finalUrl = `data:image/jpeg;base64,${raw}`;
          }
        }
        if (finalUrl) {
          detailImageCache.set(detailId, finalUrl);
          setImg(finalUrl);
        }
      })
      .catch(() => { })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [detailId]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {img ? (
        <img
          src={img}
          alt={skuNo || 'item'}
          onError={(e) => {
            // Fallback from local backend to live backend API if local image 404s
            if (!e.currentTarget.dataset.triedLive && img && !img.startsWith('data:')) {
              e.currentTarget.dataset.triedLive = 'true';
              const liveHost = 'https://api.thecaratquest.com';
              const cleanPath = img.replace(/^https?:\/\/[^\/]+/, '');
              setImg(`${liveHost}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`);
            } else {
              setImg(null);
            }
          }}
          onClick={(e) => { e.stopPropagation(); onBig && onBig({ url: img, title: `${itemName || ''} (${skuNo || ''})`, detailId }); }}
          style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 6, border: '1.5px solid var(--silver-300)', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
          title="Click to view full image"
        />
      ) : loading ? (
        <div style={{ width: 38, height: 38, borderRadius: 6, background: '#eff6ff', border: '1.5px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}
          title="Loading image...">
          <style>{`@keyframes imgSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <Loader2 size={18} style={{ animation: 'imgSpin 1s linear infinite' }} />
        </div>
      ) : (
        <div style={{ width: 38, height: 38, borderRadius: 6, background: '#f8fafc', border: '1px dashed var(--silver-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}
          title="No image">
          <Camera size={16} />
        </div>
      )}
    </div>
  );
}

// â”€â”€ Image upload cell â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function ImageUploadCell({ preview, onChange, onClear }) {
  const ref = React.useRef();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <input type="file" accept="image/*" ref={ref} style={{ display: 'none' }} onChange={onChange} />
      {preview ? (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img src={preview} alt="product"
            onClick={() => ref.current.click()}
            style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 5, border: '2px solid var(--silver-300)', cursor: 'pointer', display: 'block' }}
          />
          <button type="button" onClick={onClear}
            style={{ position: 'absolute', top: -6, right: -6, background: 'var(--red)', border: 'none', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <XCircle size={12} color="#fff" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current.click()} title="Upload photo"
          style={{ width: 34, height: 34, border: '2px dashed var(--silver-300)', borderRadius: 5, background: 'var(--silver-100)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--silver-400)', transition: 'all .15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy-600)'; e.currentTarget.style.color = 'var(--navy-600)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver-300)'; e.currentTarget.style.color = 'var(--silver-400)'; }}>
          <Camera size={14} />
        </button>
      )}
    </div>
  );
}

function NumInput({ value, onChange, placeholder = '0', step = '0.01', error = false, style = {} }) {
  return (
    <input type="number" value={value} onChange={onChange}
      placeholder={placeholder} step={step} min="0"
      style={{
        width: '100%', height: 32, minHeight: 32, boxSizing: 'border-box',
        padding: '0 8px', lineHeight: '30px',
        border: `1.5px solid ${error ? 'var(--red)' : 'var(--silver-300)'}`,
        borderRadius: 6, fontSize: 12.5,
        fontFamily: 'var(--mono)', textAlign: 'right',
        background: error ? '#fff5f5' : '#fff',
        color: 'var(--navy-800)', MozAppearance: 'textfield', ...style
      }}
      onFocus={e => e.target.style.borderColor = 'var(--navy-600)'}
      onBlur={e => e.target.style.borderColor = error ? 'var(--red)' : 'var(--silver-300)'}
    />
  );
}

// â”€â”€ Single-row table building blocks (item entry) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const GRP = {
  item: { bg: '#eff6ff', bd: '#bfdbfe', fg: '#1e40af' },
  wt: { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534' },
  price: { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' },
  sale: { bg: '#fdf2f8', bd: '#fbcfe8', fg: '#9d174d' },
  gst: { bg: '#fdf4ff', bd: '#e9d5ff', fg: '#6b21a8' },
  tot: { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534' },
  act: { bg: '#fff', bd: 'var(--silver-200)', fg: 'var(--silver-500)' },
};


const ROBox = ({ value, color = '#6b7280', bold = false, size = 12 }) => (
  <div style={{ height: 32, minHeight: 32, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', border: '1px dashed #d1d5db', borderRadius: 6, fontSize: size, fontFamily: 'var(--mono)', textAlign: 'right', background: 'rgba(255,255,255,.65)', color, fontWeight: bold ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden' }}>
    {value}
  </div>
);

const txtIn = (bc = '#bfdbfe', extra = {}) => ({
  width: '100%', height: 32, minHeight: 32, boxSizing: 'border-box',
  padding: '0 8px', lineHeight: '30px',
  border: `1.5px solid ${bc}`,
  borderRadius: 6, fontSize: 12.5, background: '#fff', color: 'var(--navy-800)', ...extra
});

// Labeled field — used by the two-row item card layout below. Each field
// carries its own small caption (was a shared <table> column header before
// the row got split in two) so the field is still self-explanatory without
// that header row.
const LF = ({ label, g, w, locked = false, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: w, minWidth: w, flexShrink: 0 }}>
    <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: locked ? '#9ca3af' : g.fg, whiteSpace: 'nowrap' }}>
      {label}
    </span>
    {children}
  </div>
);

// Colored field-group panel — groups related fields inside an item card
// (Item Details / Weight / Pricing / Sale Info / GST / Total)
const Sec = ({ title, g, children }) => (
  <div style={{ background: g.bg, border: `1px solid ${g.bd}`, borderRadius: 10, padding: '8px 12px 10px' }}>
    <div style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.07em', color: g.fg, marginBottom: 6 }}>{title}</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>{children}</div>
  </div>
);

const fmt2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt3 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export function PurchasePage() {
  const [data, setData] = useState([]);
  const [itemRows, setItemRows] = useState([]);   // per-line item view
  const [itemGroupBy, setItemGroupBy] = useState('none'); // 'vendor' | 'category' | 'none' — default: All Items (Flat)
  const [collapsedCompanyGroups, setCollapsedCompanyGroups] = useState({});
  const [globalSearch, setGlobalSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const filteredItemRows = useMemo(() => {
    let result = itemRows || [];

    if (fromDate || toDate) {
      result = result.filter(row => {
        const rawDate = row.purchaseDate || row.date || row.supplierInvDate;
        if (!rawDate) return true;
        const dStr = String(rawDate).split('T')[0];
        if (fromDate && dStr < fromDate) return false;
        if (toDate && dStr > toDate) return false;
        return true;
      });
    }

    if (globalSearch.trim()) {
      const q = globalSearch.toLowerCase();
      const searchKeys = ['vendor', 'skuNo', 'category', 'itemName', 'purchaseNo'];
      result = result.filter(row =>
        searchKeys.some(key => String(row[key] ?? '').toLowerCase().includes(q))
      );
    }

    // Show descending by SKU number (newest entry on top: 80, 79, 78 …)
    const seqNum = (r) => { const m = String(r.skuNo || '').match(/(\d+)$/); return m ? parseInt(m[1], 10) : 0; };
    return [...result].sort((a, b) => seqNum(b) - seqNum(a));
  }, [itemRows, globalSearch, fromDate, toDate]);

  const groupedItems = useMemo(() => {
    if (itemGroupBy === 'none') return null;
    const groups = {};
    (filteredItemRows || []).forEach(item => {
      const key = (itemGroupBy === 'vendor' ? item.vendor : item.category) || 'General / Unassigned';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [filteredItemRows, itemGroupBy]);
  const [view, setView] = useState('invoices'); // 'invoices' | 'items'
  // Item View row preview — click a row to see its photo, like the legacy
  // desktop app's Bag Status Report preview panel.
  const [previewRow, setPreviewRow] = useState(null);   // the clicked itemRows entry
  const [previewImg, setPreviewImg] = useState(null);   // data URI, or null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLineDetail, setPreviewLineDetail] = useState(null);
  const [itemDetailModal, setItemDetailModal] = useState(false);
  const [bigImage, setBigImage] = useState(null); // { url, title, index, loading, noImage }

  const handleOpenBigImage = (imgObj) => {
    const detailId = imgObj?.detailId;
    let idx = 0;
    if (detailId && filteredItemRows && filteredItemRows.length > 0) {
      const found = filteredItemRows.findIndex(x => (x.purchaseDetailID || x.PurchaseDetailID || x.purchasedetailid) === detailId);
      if (found !== -1) idx = found;
    }
    setBigImage({
      ...imgObj,
      index: idx
    });
  };

  const handleNavBigImage = async (dir) => {
    if (!bigImage || !filteredItemRows || filteredItemRows.length === 0) return;
    const curIdx = bigImage.index !== undefined ? bigImage.index : 0;
    let nextIdx = curIdx + dir;
    if (nextIdx < 0) nextIdx = filteredItemRows.length - 1;
    if (nextIdx >= filteredItemRows.length) nextIdx = 0;

    const nextRow = filteredItemRows[nextIdx];
    if (!nextRow) return;

    const nextTitle = `${nextRow.itemName || ''} (${nextRow.skuNo || ''})`;
    const detailId = nextRow.purchaseDetailID || nextRow.PurchaseDetailID || nextRow.purchasedetailid;

    if (detailImageCache.has(detailId)) {
      setBigImage({
        url: detailImageCache.get(detailId),
        title: nextTitle,
        index: nextIdx,
        detailId: detailId,
        loading: false
      });
      return;
    }

    setBigImage(prev => ({
      ...prev,
      title: nextTitle,
      index: nextIdx,
      detailId: detailId,
      loading: true
    }));

    try {
      const res = await purchaseAPI.getDetailImage(detailId);
      const raw = res.data?.data?.imageData || res.data?.data?.imagePath || res.data?.data?.ImagePreview || null;
      let finalUrl = null;
      if (raw) {
        if (raw.startsWith('http') || raw.startsWith('data:')) {
          finalUrl = raw;
        } else if (raw.startsWith('/') || raw.includes('/uploads/') || /\.(png|jpg|jpeg|webp|gif)$/i.test(raw)) {
          const apiHost = process.env.REACT_APP_API_URL || 'http://localhost:5002';
          finalUrl = `${apiHost}${raw.startsWith('/') ? '' : '/'}${raw}`;
        } else if (raw.length > 30) {
          finalUrl = `data:image/jpeg;base64,${raw}`;
        }
      }
      if (finalUrl) {
        detailImageCache.set(detailId, finalUrl);
      }
      setBigImage({
        url: finalUrl,
        title: nextTitle,
        index: nextIdx,
        detailId: detailId,
        loading: false,
        noImage: !finalUrl
      });
    } catch {
      setBigImage({
        url: null,
        title: nextTitle,
        index: nextIdx,
        detailId: detailId,
        loading: false,
        noImage: true
      });
    }
  };

  useEffect(() => {
    if (!bigImage) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleNavBigImage(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNavBigImage(1);
      } else if (e.key === 'Escape') {
        setBigImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [bigImage, filteredItemRows]);
  const detailsCacheRef = useRef({}); // purchaseDetailID -> single line (avoid refetching per click)
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);   // 'new' | 'edit' | false
  const [editID, setEditID] = useState(null);    // purchaseID being edited
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [collapsedItems, setCollapsedItems] = useState({});
  const [itemSteps, setItemSteps] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const itemsMap = useMemo(() => new Map(items.map(x => [Number(x.itemID), x])), [items]);

  // Quick Add Item States
  const QUICK_ADD_EMPTY = { itemName: '', categoryID: '', purityID: '', designID: '', hsnCode: '7106', gstPercent: 3, unit: 'GMS', standardWeight: 0, makingCharges: 0, barcode: '', shortCode: '', showSKUSize: false };
  const [quickAddItem, setQuickAddItem] = useState(null); // stores row index
  const [quickAddForm, setQuickAddForm] = useState(QUICK_ADD_EMPTY);
  const [quickAddSaving, setQuickAddSaving] = useState(false);

  // ── Quick-add supplier (the + next to the Supplier / Vendor label) ──
  const QUICK_SUP_EMPTY = { supplierName: '', mobile: '', gstNumber: '', city: '' };
  const [quickAddSup, setQuickAddSup] = useState(false);
  const [quickSupForm, setQuickSupForm] = useState(QUICK_SUP_EMPTY);
  const [quickSupSaving, setQuickSupSaving] = useState(false);

  const handleQuickSupSave = async (e) => {
    e.preventDefault();
    if (!quickSupForm.supplierName.trim()) { toast.error('Supplier name is required'); return; }
    setQuickSupSaving(true);
    try {
      const res = await supAPI.create(quickSupForm);
      const newID = res.data?.data?.supplierID;
      const s = await supAPI.getAll();
      setSuppliers(s.data.data || []);
      if (newID) setHeader(h => ({ ...h, supplierID: newID }));
      toast.success(`Supplier "${quickSupForm.supplierName}" added`);
      setQuickAddSup(false);
      setQuickSupForm(QUICK_SUP_EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add supplier');
    } finally { setQuickSupSaving(false); }
  };
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterPurities, setMasterPurities] = useState([]);
  const [masterCollections, setMasterCollections] = useState([]);
  const [skuConfig, setSkuConfig] = useState({ companyPrefix: 'CQ', separator: '', charCount: 2, seqPadding: 5, currentSeq: 0 });

  const generateSKUForLine = (itemObj, collection = '', productSize = '', lineIndex = 0, excludeIndex = -1) => {
    const prefix = skuConfig.companyPrefix || 'CQ';
    const charCount = Number(skuConfig.charCount) || 2;
    const seqPad = Number(skuConfig.seqPadding) || 5;
    const sep = skuConfig.separator || '';

    // Use item's ShortCode from ItemMaster as the middle segment.
    // If shortCode is set (e.g. "KD"), use it directly.
    // Otherwise fall back to deriving chars from itemName (legacy behavior).
    let itemCode = '';
    if (itemObj?.shortCode && String(itemObj.shortCode).trim()) {
      itemCode = String(itemObj.shortCode).replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, charCount);
    } else {
      const itemName = itemObj?.itemName || itemObj?.categoryName || '';
      itemCode = itemName ? itemName.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, charCount) : '';
    }

    // Size goes into the SKU only when the item master's "Show SKU Size"
    // flag is ticked; otherwise the SKU is built without it.
    let sizeCode = '';
    if (productSize && itemObj?.showSKUSize) {
      const s = String(productSize).trim();
      const matchNum = s.match(/\d+/);
      sizeCode = matchNum ? matchNum[0] : s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 4);
    }

    let genderCode = '';
    if (collection) {
      const colLower = String(collection).trim().toLowerCase();
      if (colLower.includes('wom') || colLower.includes('female') || colLower.includes('lady')) genderCode = 'W';
      else if (colLower.includes('men') || colLower.includes('male')) genderCode = 'M';
      else if (colLower.includes('uni')) genderCode = 'U';
      else genderCode = collection.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 1);
    }

    // Continue from the highest seq already used on THIS invoice's other lines
    // (so editing an invoice, item 3 follows 1 & 2 → 00003).
    // excludeIndex: skip the current line's own SKU so re-editing it doesn't bump.
    const otherSeqs = details.reduce((arr, line, idx) => {
      if (idx === excludeIndex || !line.skuNo) return arr;
      const numMatch = String(line.skuNo).match(/(\d+)$/);
      if (numMatch) arr.push(parseInt(numMatch[1], 10));
      return arr;
    }, []);
    // Continue from the highest SKU number ever used across ALL purchases, items, and lines
    // so editing an existing purchase or adding lines always increments from the global max SKU.
    let globalMaxSeq = skuConfig.currentSeq || 0;
    (itemRows || []).forEach(r => {
      const m = String(r.skuNo || '').match(/(\d+)$/);
      if (m) globalMaxSeq = Math.max(globalMaxSeq, parseInt(m[1], 10));
    });
    (items || []).forEach(r => {
      const m = String(r.skuNo || '').match(/(\d+)$/);
      if (m) globalMaxSeq = Math.max(globalMaxSeq, parseInt(m[1], 10));
    });
    (data || []).forEach(p => {
      (p.details || []).forEach(d => {
        const m = String(d.skuNo || '').match(/(\d+)$/);
        if (m) globalMaxSeq = Math.max(globalMaxSeq, parseInt(m[1], 10));
      });
    });

    const maxExistingSeq = Math.max(globalMaxSeq, ...otherSeqs, 0);

    // Reserve sequence slots only for earlier NEW lines that don't yet have a
    // SKU — lines that already have one are counted in maxExistingSeq above, so
    // counting them here too would double-count and skip a number.
    const newLinesBeforeThis = details.slice(0, lineIndex).filter((l, idx) => !l.purchaseDetailID && idx !== excludeIndex && !l.skuNo).length;

    const seq = String(maxExistingSeq + 1 + newLinesBeforeThis).padStart(seqPad, '0');

    const parts = [];
    if (prefix) parts.push(prefix);
    if (itemCode) parts.push(itemCode);
    if (sizeCode) parts.push(sizeCode);
    if (genderCode) parts.push(genderCode);
    parts.push(seq);

    return parts.join(sep);
  };

  const [header, setHeader] = useState({
    purchaseDate: todayStr(), branchID: '', supplierID: '',
    supplierInvNo: '', supplierInvDate: '', remarks: ''
  });
  const [details, setDetails] = useState([{ ...EMPTY_LINE }]);

  const load = async () => {
    setLoading(true);
    purchaseAPI.getAll()
      .then(r => setData(r.data?.data || []))
      .catch(() => toast.error('Failed to load purchase data'))
      .finally(() => setLoading(false));

    purchaseAPI.getItemView({ includeImages: false })
      .then(v => setItemRows(v.data?.data || []))
      .catch(() => setItemRows([]));
  };

  const loadMasters = async () => {
    try {
      const [s, i, b, cats, purs, colls, skuRes] = await Promise.all([
        supAPI.getAll(),
        itmAPI.getAll(),
        branchMAPI.getBranches(),
        branchMAPI.getCategories(),
        branchMAPI.getPurities(),
        branchMAPI.getCollections().catch(() => null),
        skuAPI.get().catch(() => null)
      ]);
      setSuppliers(s.data.data || []);
      setItems(i.data.data || []);
      setBranches(b.data.data || []);
      setMasterCategories(cats.data.data || []);
      setMasterPurities(purs.data.data || []);
      setMasterCollections(colls?.data?.data || []);
      if (skuRes?.data?.data) {
        const d = skuRes.data.data;
        setSkuConfig({
          companyPrefix: d.CompanyPrefix || d.companyprefix || 'CQ',
          separator: d.Separator || d.separator || '',
          charCount: d.CharCount || d.charcount || 2,
          seqPadding: d.SeqPadding || d.seqpadding || 5,
          currentSeq: d.CurrentSeq || d.currentseq || 0,
        });
      }
    } catch { }
  };

  useEffect(() => { load(); loadMasters(); }, []);
  useEffect(() => { if (view !== 'items') { setPreviewRow(null); setPreviewImg(null); } }, [view]);

  const openModal = () => {
    if (suppliers.length === 0 || items.length === 0) {
      loadMasters();
    }
    setSubmitted(false);
    setEditID(null);
    setHeader({ purchaseDate: todayStr(), branchID: branches[0]?.branchID || '', supplierID: '', supplierInvNo: '', supplierInvDate: '', remarks: '' });
    setDetails([{ ...EMPTY_LINE }]);
    setItemSteps({});
    setModal('new');
  };

  const openEdit = (row) => {
    setSubmitted(false);
    setEditID(row.purchaseID);
    setHeader({
      purchaseDate: row.purchaseDate?.split('T')[0] || todayStr(),
      branchID: String(row.branchID || ''),
      supplierID: String(row.supplierID || ''),
      supplierInvNo: row.supplierInvNo || '',
      supplierInvDate: row.supplierInvDate?.split('T')[0] || '',
      remarks: row.remarks || '',
    });

    setDetails([]);
    setDetailsLoading(true);
    setItemSteps({});
    setModal('edit'); // Open modal INSTANTLY (0ms delay)!

    // Fetch full details (Category, ItemID, SKU, Image) without heavy base64 strings upfront
    purchaseAPI.getById(row.purchaseID, { includeImages: false }).then(res => {
      const dataObj = res.data?.data || {};
      const det = dataObj.details || dataObj.Details || (Array.isArray(dataObj) ? dataObj : []);
      if (det && det.length > 0) {
        const seqNum = (d) => {
          const sku = d.skuNo || d.SKUNo || d.skuno || '';
          const m = String(sku).match(/(\d+)$/);
          return m ? parseInt(m[1], 10) : (d.purchaseDetailID || d.PurchaseDetailID || 0);
        };
        const sortedDet = [...det].sort((a, b) => {
          const sA = seqNum(a);
          const sB = seqNum(b);
          if (sA !== sB) return sB - sA; // Descending: newest entry on top (80, 79, 78...)
          return (b.purchaseDetailID || b.PurchaseDetailID || 0) - (a.purchaseDetailID || a.PurchaseDetailID || 0);
        });
        setCollapsedItems({}); // Keep lines open/expanded by default as before
        const mappedLines = sortedDet.map(d => {
          const itemID = String(d.itemID || d.ItemID || d.itemId || '');
          const rawImg = d.imageData || d.ImageData || d.imagedata || d.imagePreview || d.ImagePreview || d.imagePath || d.ImagePath || d.imagepath || null;
          const img = rawImg ? (rawImg.startsWith('http') || rawImg.startsWith('data:') ? rawImg : `${process.env.REACT_APP_API_URL || 'http://localhost:5002'}${rawImg}`) : null;
          const pDetId = d.purchaseDetailID || d.PurchaseDetailID || null;
          const cachedImg = pDetId && detailImageCache.has(pDetId) ? detailImageCache.get(pDetId) : null;
          return {
            purchaseDetailID: pDetId,
            itemID: itemID,
            vendorSRNo: d.vendorSRNo || d.VendorSRNo || '',
            skuNo: d.skuNo || d.SKUNo || d.skuno || '',
            collection: d.collection || d.Collection || '',
            productSize: d.productSize || d.ProductSize || d.productsize || '',
            description: d.description || d.Description || '',
            quantity: d.quantity ?? d.Quantity ?? 1,
            grossWeight: d.grossWeight ?? d.GrossWeight ?? 0,
            weight: d.weight ?? d.Weight ?? 0,
            labourPerGram: d.labourPerGram ?? d.LabourPerGram ?? 0,
            silverRatePerGram: d.silverRatePerGram ?? d.SilverRatePerGram ?? 0,
            rate: d.rate ?? d.Rate ?? 0,
            cogs: d.cogs ?? d.COGS ?? 0,
            mrp: d.mrp ?? d.MRP ?? 0,
            setComboPrice: d.setComboPrice ?? d.SetComboPrice ?? '',
            discount: d.discount ?? d.Discount ?? 0,
            salePrice: d.salePrice ?? d.SalePrice ?? 0,
            fixedExpenses: d.fixedExpenses ?? d.FixedExpenses ?? 0,
            otherExpenses: d.otherExpenses ?? d.OtherExpenses ?? 0,
            productStatus: d.productStatus || d.ProductStatus || 'IN STOCK',
            gstPercent: d.gstPercent ?? d.GSTPercent ?? 3,
            isIGST: d.isIGST ?? d.IsIGST ?? false,
            imagePreview: img || cachedImg,
            imageFile: null,
            imageBase64: img || cachedImg,
          };
        });
        setDetails(mappedLines);

        // Fetch line images on-demand in background for each line
        mappedLines.forEach((line, idx) => {
          if (line.purchaseDetailID && !line.imagePreview) {
            purchaseAPI.getDetailImage(line.purchaseDetailID).then(res => {
              const raw = res.data?.data?.imageData || res.data?.data?.imagePath || null;
              let finalUrl = null;
              if (raw) {
                if (raw.startsWith('http') || raw.startsWith('data:')) {
                  finalUrl = raw;
                } else if (raw.startsWith('/') || raw.includes('/uploads/')) {
                  const apiHost = process.env.REACT_APP_API_URL || 'http://localhost:5002';
                  finalUrl = `${apiHost}${raw.startsWith('/') ? '' : '/'}${raw}`;
                } else if (raw.length > 30) {
                  finalUrl = `data:image/jpeg;base64,${raw}`;
                }
              }
              if (finalUrl) {
                detailImageCache.set(line.purchaseDetailID, finalUrl);
                setDetails(prev => prev.map(item => (
                  (item.purchaseDetailID || item.PurchaseDetailID) === line.purchaseDetailID
                    ? { ...item, imagePreview: finalUrl, imageBase64: finalUrl }
                    : item
                )));
              }
            }).catch(() => { });
          }
        });
      }
    }).catch(() => {
      toast.error('Failed to load invoice details');
    }).finally(() => {
      setDetailsLoading(false);
    });
  };

  // Which piece the publish popup is open on, if any
  const [webModalRow, setWebModalRow] = useState(null);

  const openWebModal = (row) => setWebModalRow(row);

  const openPreview = async (row) => {
    setPreviewRow(row);
    setPreviewImg(detailImageCache.get(row.purchaseDetailID) || null);
    setPreviewLineDetail(null);
    setPreviewLoading(true);
    setItemDetailModal(true);

    try {
      let line = detailsCacheRef.current[row.purchaseDetailID];
      if (!line) {
        const res = await purchaseAPI.getDetail(row.purchaseDetailID);
        line = res.data?.data || null;
        if (line) detailsCacheRef.current[row.purchaseDetailID] = line;
      }
      setPreviewLineDetail(line || null);
      const rawImg = line?.imageData || line?.ImageData || line?.imagedata || line?.imagePath || line?.ImagePath || line?.imagepath || null;
      let img = null;
      if (rawImg) {
        if (rawImg.startsWith('http') || rawImg.startsWith('data:')) {
          img = rawImg;
        } else if (rawImg.startsWith('/') || rawImg.includes('/uploads/')) {
          const apiHost = process.env.REACT_APP_API_URL || 'http://localhost:5002';
          img = `${apiHost}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`;
        } else if (rawImg.length > 30) {
          img = `data:image/jpeg;base64,${rawImg}`;
        }
      }
      const finalImg = img || detailImageCache.get(row.purchaseDetailID) || null;
      if (finalImg) detailImageCache.set(row.purchaseDetailID, finalImg);
      setPreviewImg(finalImg);
    } catch {
      setPreviewImg(detailImageCache.get(row.purchaseDetailID) || null);
      setPreviewLineDetail(null);
    }
    finally { setPreviewLoading(false); }
  };

  const closePreview = () => { setPreviewRow(null); setPreviewImg(null); setPreviewLineDetail(null); setItemDetailModal(false); };

  const [confirmDelete, setConfirmDelete] = useState(null); // { id, no }

  // ── Tree view: per-invoice item details ──
  // Built instantly from the already-loaded item-view rows (no per-expand API
  // call). getById was slow because it returns every line's base64 image (up to
  // ~10MB each) which this summary panel doesn't need.
  const [invItems, setInvItems] = useState({}); // purchaseID -> { rows }
  const loadInvItems = (row) => {
    const id = row.purchaseID || row.PurchaseID || row.purchaseid;
    if (!id) return;
    if (invItems[id]?.rows && invItems[id].rows.length > 0) return;
    const seqNum = (r) => {
      const m = String(r.skuNo || r.SKUNo || '').match(/(\d+)$/);
      return m ? parseInt(m[1], 10) : (r.purchaseDetailID || r.PurchaseDetailID || 0);
    };
    const rows = (itemRows || []).filter(r => (r.purchaseID || r.PurchaseID || r.purchaseid) === id).sort((a, b) => seqNum(b) - seqNum(a));
    if (rows.length > 0) {
      setInvItems(p => ({ ...p, [id]: { loading: false, rows } }));
    } else {
      setInvItems(p => ({ ...p, [id]: { loading: true, rows: [] } }));
      purchaseAPI.getById(id).then(res => {
        const details = (res.data?.data?.details || []).sort((a, b) => seqNum(b) - seqNum(a));
        setInvItems(p => ({ ...p, [id]: { loading: false, rows: details } }));
      }).catch(() => {
        setInvItems(p => ({ ...p, [id]: { loading: false, rows: [], error: true } }));
      });
    }
  };

  const invItemsPanel = (row) => {
    const st = invItems[row.purchaseID];
    if (!st || st.loading) return <div style={{ padding: 18, textAlign: 'center', color: 'var(--silver-500)' }}><span className="spinner" style={{ width: 18, height: 18 }} /></div>;
    if (st.error) return <div style={{ padding: 18, textAlign: 'center', color: 'var(--red)' }}>Failed to load items</div>;
    if (!st.rows.length) return <div style={{ padding: 18, textAlign: 'center', color: 'var(--silver-500)' }}>No items on this invoice</div>;
    const thS = { padding: '7px 12px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--silver-500)', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--silver-200, #e2e8f0)' };
    const thSR = { ...thS, textAlign: 'right' };
    const tdS = { padding: '7px 12px', fontSize: 12.5, whiteSpace: 'nowrap', borderBottom: '1px solid #eef2f7' };
    const tdSR = { ...tdS, textAlign: 'right', fontFamily: 'var(--mono)' };
    return (
      <div style={{ padding: '10px 16px 14px 48px' }}>
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--silver-200, #e2e8f0)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thS}>#</th><th style={thS}>SKU No</th><th style={thS}>Item</th>
              <th style={thS}>Collection</th><th style={thS}>Size</th>
              <th style={thSR}>Qty</th><th style={thSR}>Net Wt</th><th style={thSR}>Rate/g</th>
              <th style={thSR}>Purch. Amt</th><th style={thSR}>Purch. (3%)</th><th style={thSR}>MRP</th><th style={thSR}>Discount</th><th style={thSR}>Sale Price</th>
            </tr></thead>
            <tbody>
              {st.rows.map((it, ix) => {
                const q = Number(it.quantity || 1);
                const purAmt = Number(it.rate || 0) * Number(it.weight || 0) * q;
                const purAmt3 = purAmt * 1.03;
                return (
                  <tr key={it.purchaseDetailID || ix}>
                    <td style={tdS}>{ix + 1}</td>
                    <td style={tdS}><code style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--primary)' }}>{it.skuNo || '—'}</code></td>
                    <td style={{ ...tdS, fontWeight: 600 }}>{it.itemName}{it.purityCode ? ` (${it.purityCode})` : ''}{it.description ? <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--silver-500)' }}>{it.description}</div> : null}</td>
                    <td style={tdS}>{it.collection || '—'}</td>
                    <td style={tdS}>{it.productSize || '—'}</td>
                    <td style={tdSR}>{Number(it.quantity || 0)}</td>
                    <td style={tdSR}>{fmt3(it.weight)} g</td>
                    <td style={tdSR}>₹{fmt2(it.rate)}</td>
                    <td style={tdSR}>₹{fmt2(purAmt)}</td>
                    <td style={{ ...tdSR, fontWeight: 700, color: '#1e40af', background: '#f0f9ff' }}>₹{fmt2(purAmt3)}</td>
                    <td style={tdSR}>₹{fmt2(it.mrp)}</td>
                    <td style={{ ...tdSR, color: it.discount > 0 ? 'var(--red)' : undefined }}>{it.discount > 0 ? `₹${fmt2(it.discount)}` : '—'}</td>
                    <td style={{ ...tdSR, fontWeight: 700 }}>₹{fmt2(it.salePrice)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Record Payment ──────────────────────────────────────
  const [payModal, setPayModal] = useState(null); // invoice row being paid
  const [payForm, setPayForm] = useState({ amount: '', paymentMode: 'CASH', remarks: '' });
  const [paying, setPaying] = useState(false);

  const openPayModal = (row) => {
    setPayForm({ amount: row.balanceAmount > 0 ? row.balanceAmount : '', paymentMode: 'CASH', remarks: '' });
    setPayModal(row);
  };

  // ── Reverse Payment ─────────────────────────────────────
  const [reverseModal, setReverseModal] = useState(null); // invoice row
  const [reverseForm, setReverseForm] = useState({ amount: '', reason: '' });
  const [reversing, setReversing] = useState(false);

  const openReverseModal = (row) => {
    setReverseForm({ amount: row.paidAmount > 0 ? row.paidAmount : '', reason: '' });
    setReverseModal(row);
  };

  const handleReverse = async (e) => {
    e.preventDefault();
    const amt = Number(reverseForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > Number(reverseModal.paidAmount)) { toast.error('Amount cannot exceed paid amount'); return; }
    setReversing(true);
    try {
      const res = await purchaseAPI.reversePayment(reverseModal.purchaseID, {
        amount: amt, reason: reverseForm.reason || null,
      });
      toast.success(`Payment reversed — status: ${res.data.data?.status || 'updated'}`);
      setReverseModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reverse payment');
    } finally { setReversing(false); }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > Number(payModal.balanceAmount)) { toast.error('Amount cannot exceed balance'); return; }
    setPaying(true);
    try {
      const res = await purchaseAPI.recordPayment(payModal.purchaseID, {
        amount: amt, paymentMode: payForm.paymentMode, remarks: payForm.remarks || null,
      });
      toast.success(`Payment recorded — status: ${res.data.data?.status || 'updated'}`);
      setPayModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally { setPaying(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await purchaseAPI.delete(confirmDelete.id);
      toast.success(`Invoice ${confirmDelete.no} deleted`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const addLine = () => setDetails(d => [{ ...EMPTY_LINE }, ...d]);
  const removeLine = (i) => setDetails(d => d.filter((_, idx) => idx !== i));
  const rebuildSKUForLine = (r, newSize, newCollection, itemObj) => {
    if (r.isManualSKU || !r.skuNo) return r.skuNo;
    const prefix = skuConfig.companyPrefix || 'CQ';
    const charCount = Number(skuConfig.charCount) || 2;
    const sep = skuConfig.separator || '';

    // Extract existing end sequence (e.g. "00074" from "CQRG16W00074")
    const seqMatch = String(r.skuNo).match(/(\d+)$/);
    if (!seqMatch) return r.skuNo;
    const existingSeqStr = seqMatch[1];

    let itemCode = '';
    if (itemObj?.shortCode && String(itemObj.shortCode).trim()) {
      itemCode = String(itemObj.shortCode).replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, charCount);
    } else {
      const itemName = itemObj?.itemName || itemObj?.categoryName || '';
      itemCode = itemName ? itemName.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, charCount) : '';
    }

    let sizeCode = '';
    const targetSize = newSize !== undefined ? newSize : r.productSize;
    if (targetSize && itemObj?.showSKUSize) {
      const s = String(targetSize).trim();
      const matchNum = s.match(/\d+/);
      sizeCode = matchNum ? matchNum[0] : s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 4);
    }

    let genderCode = '';
    const targetCol = newCollection !== undefined ? newCollection : r.collection;
    if (targetCol) {
      const colLower = String(targetCol).trim().toLowerCase();
      if (colLower.includes('wom') || colLower.includes('female') || colLower.includes('lady')) genderCode = 'W';
      else if (colLower.includes('men') || colLower.includes('male')) genderCode = 'M';
      else if (colLower.includes('uni')) genderCode = 'U';
      else genderCode = targetCol.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 1);
    }

    const parts = [];
    if (prefix) parts.push(prefix);
    if (itemCode) parts.push(itemCode);
    if (sizeCode) parts.push(sizeCode);
    if (genderCode) parts.push(genderCode);
    parts.push(existingSeqStr);

    return parts.join(sep);
  };

  const updateLine = (i, key, val) => {
    setDetails(prev => prev.map((r, idx) => {
      if (idx !== i) return r;
      const updated = { ...r, [key]: val };
      if (key === 'skuNo') {
        updated.isManualSKU = true;
      } else if (key === 'collection' || key === 'productSize') {
        const itemObj = items.find(it => it.itemID === +r.itemID);
        if (r.skuNo) {
          // Rebuild SKU size/collection segment while preserving the exact end sequence number (e.g. 00074)
          updated.skuNo = rebuildSKUForLine(r, key === 'productSize' ? val : r.productSize, key === 'collection' ? val : r.collection, itemObj);
        } else if (!r.isManualSKU && !r.purchaseDetailID) {
          // Brand new line without SKU yet — generate fresh SKU
          updated.skuNo = generateSKUForLine(itemObj, key === 'collection' ? val : r.collection, key === 'productSize' ? val : r.productSize, i, i);
        }
      }
      return updated;
    }));
  };

  const onItemSelect = (i, itemID) => {
    const item = items.find(it => it.itemID === +itemID);
    setDetails(prev => prev.map((r, idx) => {
      if (idx !== i) return r;
      const keepExistingSKU = r.purchaseDetailID || r.skuNo || r.isManualSKU;
      const autoSKU = keepExistingSKU ? rebuildSKUForLine(r, r.productSize, r.collection, item) : generateSKUForLine(item, r.collection, r.productSize, i, i);
      return {
        ...r,
        itemID,
        gstPercent: item?.gstPercent ?? r.gstPercent,
        mrp: item?.mrp ?? r.mrp,
        skuNo: autoSKU,
        collection: item?.collection ?? r.collection,
        productSize: item?.productSize ?? r.productSize,
      };
    }));
  };

  const handleQuickAddSave = async (e) => {
    e.preventDefault();
    if (!quickAddForm.itemName || !quickAddForm.categoryID || !quickAddForm.purityID) {
      toast.error('Name, Category Group, and Purity are required');
      return;
    }
    setQuickAddSaving(true);
    try {
      const payload = {
        ...quickAddForm,
        categoryID: +quickAddForm.categoryID,
        purityID: +quickAddForm.purityID,
        designID: quickAddForm.designID ? +quickAddForm.designID : null,
        shortCode: quickAddForm.shortCode || '',
        showSKUSize: !!quickAddForm.showSKUSize
      };
      const res = await itmAPI.create(payload);
      toast.success('Category/Item created!');

      const i = await itmAPI.getAll();
      const updated = i.data.data || [];
      setItems(updated);

      const created = res.data.data || updated.find(x => x.itemName === quickAddForm.itemName);
      if (created) {
        onItemSelect(quickAddItem, String(created.itemID));
      }
      setQuickAddItem(null);
      setQuickAddForm(QUICK_ADD_EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create category/item');
    } finally {
      setQuickAddSaving(false);
    }
  };

  const handleImageChange = async (i, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    try {
      const base64 = await toBase64(file);
      setDetails(d => d.map((r, idx) => idx === i
        ? { ...r, imageFile: file, imagePreview: base64, imageBase64: base64 } : r));
    } catch { toast.error('Failed to load image'); }
  };

  const clearImage = (i) => setDetails(d => d.map((r, idx) =>
    idx === i ? { ...r, imageFile: null, imagePreview: null, imageBase64: null } : r));

  // Auto-calcs: rate/g = silver + labour (COGS is entered manually by user)
  const autoCalcRate = (i, field, val) => {
    setDetails(prev => {
      const next = prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r);
      const row = next[i];
      const sr = Number(field === 'silverRatePerGram' ? val : row.silverRatePerGram || 0);
      const lb = Number(field === 'labourPerGram' ? val : row.labourPerGram || 0);
      if (sr > 0 || lb > 0) {
        const rate = sr + lb;
        next[i] = { ...next[i], rate: rate.toFixed(2) };
      }
      return next;
    });
  };

  // Weight is entered PER PIECE — totals scale by quantity
  const calcTotals = () => {
    let totalWeight = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0;
    details.forEach(d => {
      const qty = Number(d.quantity) > 0 ? Number(d.quantity) : 1;
      const t = Number(d.weight || 0) * Number(d.rate || 0) * qty;
      // GST is charged on the sales Base amount (Sale Price ÷ 1.03), not the Taxable
      const mrp = Number(d.mrp || 0); const disc = Number(d.discount || 0);
      const autoSale = mrp > 0 ? Math.max(0, mrp - disc) : Number(d.salePrice || 0);
      const base = autoSale > 0 ? autoSale / 1.03 : 0;
      const half = base * Number(d.gstPercent || 0) / 2 / 100;
      totalWeight += Number(d.weight || 0) * qty;
      taxable += t;
      if (d.isIGST) igst += half * 2; else { cgst += half; sgst += half; }
    });
    return { totalWeight, taxable, cgst, sgst, igst, total: taxable + cgst + sgst + igst };
  };

  const totals = calcTotals();

  const validate = () => {
    const errs = [];
    if (!header.purchaseDate) errs.push('Purchase date required');
    if (!header.branchID) errs.push('Select branch');
    if (!header.supplierID) errs.push('Select supplier');
    details.forEach((d, i) => {
      if (!d.itemID) errs.push(`Row ${i + 1}: Select category`);
      if (!d.weight || +d.weight <= 0) errs.push(`Row ${i + 1}: Net weight required`);
      if (!d.rate || +d.rate <= 0) errs.push(`Row ${i + 1}: Enter Labour & Silver/g (purchase price)`);
    });
    return errs;
  };

  const handleSaveLineAndAddNext = async (i) => {
    setSubmitted(true);
    if (!header.purchaseDate) { toast.error('Purchase date required'); return; }
    if (!header.branchID) { toast.error('Select branch'); return; }
    if (!header.supplierID) { toast.error('Select supplier'); return; }

    const targetLine = details[i];
    if (!targetLine) return;
    if (!targetLine.itemID) { toast.error(`Item #${i + 1}: Select category`); return; }
    if (!targetLine.weight || +targetLine.weight <= 0) { toast.error(`Item #${i + 1}: Net weight required`); return; }
    if (!targetLine.rate || +targetLine.rate <= 0) { toast.error(`Item #${i + 1}: Enter Labour & Silver/g`); return; }

    setSaving(true);
    try {
      const buildDetail = (d) => {
        const qty = Number(d.quantity) > 0 ? Number(d.quantity) : 1;
        const taxable = +d.weight * +d.rate * qty;
        const autoSaleB = +d.mrp > 0 ? Math.max(0, +d.mrp - +d.discount) : (+d.salePrice || 0);
        const baseB = autoSaleB > 0 ? autoSaleB / 1.03 : 0;
        const cgst = d.isIGST ? 0 : Math.round(baseB * (+d.gstPercent / 2 / 100) * 100) / 100;
        const igst = d.isIGST ? Math.round(baseB * (+d.gstPercent / 100) * 100) / 100 : 0;
        return {
          purchaseDetailID: d.purchaseDetailID || null,
          itemID: +d.itemID, vendorSRNo: d.vendorSRNo || null,
          skuNo: d.skuNo || null, collection: d.collection || null,
          productSize: d.productSize || null, description: d.description || null,
          quantity: +d.quantity, grossWeight: +d.grossWeight || 0,
          weight: +d.weight, labourPerGram: +d.labourPerGram || 0,
          silverRatePerGram: +d.silverRatePerGram || 0,
          rate: +d.rate, cogs: +d.cogs || 0, mrp: +d.mrp || 0,
          setComboPrice: d.setComboPrice ? +d.setComboPrice : null,
          discount: +d.discount || 0, salePrice: +d.salePrice || 0,
          fixedExpenses: +d.fixedExpenses || 0, otherExpenses: +d.otherExpenses || 0,
          productStatus: d.productStatus || 'IN STOCK',
          gstPercent: +d.gstPercent, isIGST: d.isIGST,
          cgstPercent: d.isIGST ? 0 : +d.gstPercent / 2,
          sgstPercent: d.isIGST ? 0 : +d.gstPercent / 2,
          cgstAmount: cgst, sgstAmount: cgst,
          totalAmount: taxable + cgst * 2 + igst,
          imageBase64: d.imageBase64 || d.imagePreview || null,
        };
      };

      const payload = {
        ...header,
        branchID: +header.branchID, supplierID: +header.supplierID,
        supplierInvDate: header.supplierInvDate || null,
        totalWeight: totals.totalWeight, taxableAmount: totals.taxable,
        cgstAmount: totals.cgst, sgstAmount: totals.sgst, igstAmount: totals.igst,
        totalAmount: totals.total,
        details: details.map(buildDetail),
      };

      let currentID = editID;
      if (currentID) {
        await purchaseAPI.update(currentID, payload);
      } else {
        const res = await purchaseAPI.create(payload);
        currentID = res.data.data?.purchaseID;
        setEditID(currentID);
      }

      const catObj = items.find(it => it.itemID === +targetLine.itemID);
      const catName = catObj ? catObj.itemName : '';
      toast.success(`✅ Item Saved to DB! ${catName ? `(${catName} - ` : '('}SKU: ${targetLine.skuNo || 'Created'})`);

      if (currentID) {
        const invRes = await purchaseAPI.getById(currentID, { includeImages: false });
        const fetchedDetails = invRes.data.data?.details || [];
        if (fetchedDetails.length > 0) {
          const seqNum = (d) => {
            const sku = d.skuNo || d.SKUNo || d.skuno || '';
            const m = String(sku).match(/(\d+)$/);
            return m ? parseInt(m[1], 10) : (d.purchaseDetailID || d.PurchaseDetailID || 0);
          };
          const sortedDet = [...fetchedDetails].sort((a, b) => {
            const sA = seqNum(a);
            const sB = seqNum(b);
            if (sA !== sB) return sB - sA; // Descending: newest entry on top (155, 154, 153...)
            return (b.purchaseDetailID || b.PurchaseDetailID || 0) - (a.purchaseDetailID || a.PurchaseDetailID || 0);
          });
          const mapped = sortedDet.map(d => {
            const pDetId = d.purchaseDetailID || d.PurchaseDetailID || null;
            const existingLine = details.find(x => (pDetId && x.purchaseDetailID === pDetId) || (x.skuNo && x.skuNo === (d.skuNo || d.SKUNo)));
            const cachedImg = pDetId && detailImageCache.has(pDetId) ? detailImageCache.get(pDetId) : null;
            const rawImg = d.imageData || d.imagedata || d.ImageData || (d.imagePath ? (d.imagePath.startsWith('http') || d.imagePath.startsWith('data:') ? d.imagePath : `${process.env.REACT_APP_API_URL || 'http://localhost:5002'}${d.imagePath}`) : null);
            const img = rawImg || existingLine?.imagePreview || existingLine?.imageBase64 || cachedImg || null;
            return {
              purchaseDetailID: pDetId,
              itemID: String(d.itemID || d.ItemID || d.itemId || ''),
              vendorSRNo: d.vendorSRNo || d.VendorSRNo || '',
              skuNo: d.skuNo || d.SKUNo || d.skuno || '',
              collection: d.collection || d.Collection || '',
              productSize: d.productSize || d.ProductSize || d.productsize || '',
              description: d.description || d.Description || '',
              quantity: d.quantity ?? d.Quantity ?? 1,
              grossWeight: d.grossWeight ?? d.GrossWeight ?? 0,
              weight: d.weight ?? d.Weight ?? 0,
              labourPerGram: d.labourPerGram ?? d.LabourPerGram ?? 0,
              silverRatePerGram: d.silverRatePerGram ?? d.SilverRatePerGram ?? 0,
              rate: d.rate ?? d.Rate ?? 0,
              cogs: d.cogs ?? d.COGS ?? 0,
              mrp: d.mrp ?? d.MRP ?? 0,
              setComboPrice: d.setComboPrice ?? d.SetComboPrice ?? '',
              discount: d.discount ?? d.Discount ?? 0,
              salePrice: d.salePrice ?? d.SalePrice ?? 0,
              fixedExpenses: d.fixedExpenses ?? d.FixedExpenses ?? 0,
              otherExpenses: d.otherExpenses ?? d.OtherExpenses ?? 0,
              productStatus: d.productStatus || d.ProductStatus || 'IN STOCK',
              gstPercent: d.gstPercent ?? d.GSTPercent ?? 3,
              isIGST: d.isIGST ?? d.IsIGST ?? false,
              imagePreview: img,
              imageFile: existingLine?.imageFile || null,
              imageBase64: img,
              isSavedInDB: true
            };
          });
          setDetails([{ ...EMPTY_LINE }, ...mapped]);
        } else {
          setDetails(d => [{ ...EMPTY_LINE }, ...d]);
        }
      } else {
        setDetails(d => [{ ...EMPTY_LINE }, ...d]);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate();
    if (errs.length > 0) { toast.error(errs[0]); return; }
    setSaving(true);
    try {
      const buildDetail = (d) => {
        const qty = Number(d.quantity) > 0 ? Number(d.quantity) : 1;
        const taxable = +d.weight * +d.rate * qty;   // weight is per piece
        // GST is charged on the sales Base amount (Sale Price ÷ 1.03), not the Taxable
        const autoSaleB = +d.mrp > 0 ? Math.max(0, +d.mrp - +d.discount) : (+d.salePrice || 0);
        const baseB = autoSaleB > 0 ? autoSaleB / 1.03 : 0;
        const cgst = d.isIGST ? 0 : Math.round(baseB * (+d.gstPercent / 2 / 100) * 100) / 100;
        const igst = d.isIGST ? Math.round(baseB * (+d.gstPercent / 100) * 100) / 100 : 0;
        return {
          purchaseDetailID: d.purchaseDetailID || null,
          itemID: +d.itemID, vendorSRNo: d.vendorSRNo || null,
          skuNo: d.skuNo || null, collection: d.collection || null,
          productSize: d.productSize || null, description: d.description || null,
          quantity: +d.quantity, grossWeight: +d.grossWeight || 0,
          weight: +d.weight, labourPerGram: +d.labourPerGram || 0,
          silverRatePerGram: +d.silverRatePerGram || 0,
          rate: +d.rate, cogs: +d.cogs || 0, mrp: +d.mrp || 0,
          setComboPrice: d.setComboPrice ? +d.setComboPrice : null,
          discount: +d.discount || 0, salePrice: +d.salePrice || 0,
          fixedExpenses: +d.fixedExpenses || 0, otherExpenses: +d.otherExpenses || 0,
          productStatus: d.productStatus || 'IN STOCK',
          gstPercent: +d.gstPercent, isIGST: d.isIGST,
          cgstPercent: d.isIGST ? 0 : +d.gstPercent / 2,
          sgstPercent: d.isIGST ? 0 : +d.gstPercent / 2,
          cgstAmount: cgst, sgstAmount: cgst,
          totalAmount: taxable + cgst * 2 + igst,
          imageBase64: d.imageBase64 || null,
        };
      };
      const payload = {
        ...header,
        branchID: +header.branchID, supplierID: +header.supplierID,
        supplierInvDate: header.supplierInvDate || null,
        totalWeight: totals.totalWeight, taxableAmount: totals.taxable,
        cgstAmount: totals.cgst, sgstAmount: totals.sgst, igstAmount: totals.igst,
        totalAmount: totals.total,
        details: details.map(buildDetail),
      };
      if (editID) {
        await purchaseAPI.update(editID, payload);
        toast.success('Purchase invoice updated!');
      } else {
        await purchaseAPI.create(payload);
        toast.success('Purchase invoice created!');
      }
      setModal(false); setSubmitted(false); setEditID(null); load();
    } catch (err) {
      toast.error(err.response?.data?.message || (editID ? 'Failed to update' : 'Failed to create'));
    } finally { setSaving(false); }
  };

  const errs = submitted ? validate() : [];

  const statusBadge = (s) => {
    const map = { PAID: 'badge-green', PARTIAL: 'badge-amber', UNPAID: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-navy'}`}>{s}</span>;
  };

  // Item view — one row per purchase line (Excel style)
  const itemColumns = [
    {
      header: 'Date', key: 'purchaseDate', render: r => (
        <span style={{ whiteSpace: 'nowrap', color: 'var(--silver-500)', fontSize: 13 }}>
          {new Date(r.purchaseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
        </span>
      )
    },
    {
      header: 'SKU No', key: 'skuNo', render: r => r.skuNo ? (
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, color: '#3730a3', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
          {r.skuNo}
        </span>
      ) : <span className="text-muted">—</span>
    },
    {
      header: 'Image', key: '_image', align: 'center', render: r => (
        <LazyDetailImage
          detailId={r.purchaseDetailID || r.PurchaseDetailID || r.purchasedetailid}
          skuNo={r.skuNo}
          itemName={r.itemName}
          onBig={handleOpenBigImage}
        />
      )
    },
    {
      header: 'Item', key: 'itemName', render: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.itemName}</div>
          <div style={{ fontSize: 11, color: 'var(--silver-500)' }}>{r.vendor}</div>
        </div>
      )
    },
    { header: 'Qty', key: 'quantity', align: 'center', render: r => <span className="badge badge-navy">{Number(r.quantity)}</span> },
    { header: 'Purch. Price', key: 'purchasePrice', align: 'right', render: r => <span className="text-mono">₹{fmt2(r.purchasePrice)}</span> },
    { header: 'Purch. (3%)', key: 'purchasePrice3', align: 'right', render: r => <span className="text-mono" style={{ fontWeight: 700, color: '#1e40af' }}>₹{fmt2(Number(r.purchasePrice || 0) * 1.03)}</span> },
    { header: 'MRP', key: 'mrp', align: 'right', render: r => <span className="text-mono" style={{ color: 'var(--silver-500)' }}>₹{fmt2(r.mrp)}</span> },
    { header: 'Discount', key: 'discount', align: 'right', render: r => r.discount > 0 ? <span className="text-mono" style={{ color: 'var(--red)' }}>- ₹{fmt2(r.discount)}</span> : <span className="text-muted">—</span> },
    { header: 'Sale ₹', key: 'salePrice', align: 'right', render: r => <span className="text-mono" style={{ fontWeight: 700, color: '#be185d' }}>₹{fmt2(r.salePrice)}</span> },
    { header: 'T.Exp', key: 'totalExpenses', align: 'right', render: r => r.totalExpenses > 0 ? <span className="text-mono" style={{ color: '#7c3aed' }}>₹{fmt2(r.totalExpenses)}</span> : <span className="text-muted">—</span> },
    {
      header: 'Net Amt', key: 'netAmount', align: 'right', render: r => (
        <span className="text-mono" style={{ fontWeight: 700, color: r.netAmount > 0 ? '#0f766e' : r.netAmount < 0 ? 'var(--red)' : undefined }}>₹{fmt2(r.netAmount)}</span>
      )
    },
    {
      header: 'Net Pr%', key: 'netProfitPct', align: 'right', render: r => (
        <span style={{
          fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 10, whiteSpace: 'nowrap',
          color: r.netProfitPct > 0 ? '#0f766e' : r.netProfitPct < 0 ? 'var(--red)' : 'var(--silver-500)',
          background: r.netProfitPct > 0 ? '#f0fdfa' : r.netProfitPct < 0 ? 'var(--red-light)' : 'var(--silver-100)',
        }}>
          {Number(r.netProfitPct || 0).toFixed(2)}%
        </span>
      )
    },
    {
      header: 'Actions', key: '_actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
          <button
            className="btn btn-ghost btn-sm btn-icon"
            title={r.showOnWebsite ? "Published to website (Click to view/edit website details)" : "Click to publish on website"}
            style={{ color: r.showOnWebsite ? '#16a34a' : '#94a3b8', background: r.showOnWebsite ? '#f0fdf4' : 'transparent' }}
            onClick={(e) => {
              e.stopPropagation();
              openWebModal(r);
            }}
          >
            <Globe size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="View Full Details"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => openPreview(r)}>
            <Eye size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Print Tag Sticker"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => printItemTag({ ...r, price: r.salePrice || r.mrp })}>
            <Printer size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit Invoice"
            onClick={() => { const row = data.find(x => x.purchaseID === r.purchaseID); if (row) openEdit(row); }}>
            <Edit2 size={14} />
          </button>
        </div>
      )
    },
  ];

  // Searchable SKU string per invoice, derived from the in-memory item rows so
  // SKU search works without expanding each invoice.
  const rowsWithSku = useMemo(() => {
    const skuMap = new Map();
    (itemRows || []).forEach(it => {
      if (it?.purchaseID && it?.skuNo) {
        const existing = skuMap.get(it.purchaseID) || '';
        skuMap.set(it.purchaseID, existing ? `${existing} ${it.skuNo}` : it.skuNo);
      }
    });
    return data.map(r => ({
      ...r,
      _skuHints: skuMap.get(r.purchaseID) || '',
    }));
  }, [data, itemRows]);

  const columns = [
    { header: 'Purchase No', key: 'purchaseNo', render: r => <code style={{ fontSize: 12 }}>{r.purchaseNo}</code> },
    { header: 'Date', key: 'purchaseDate', render: r => new Date(r.purchaseDate).toLocaleDateString('en-IN') },
    { header: 'Supplier', key: 'supplierName', render: r => <span style={{ fontWeight: 600 }}>{r.supplierName}</span> },
    { header: 'Supp. Inv.', key: 'supplierInvNo', render: r => r.supplierInvNo || <span className="text-muted">—</span> },
    {
      header: 'Items', key: 'totalItems', align: 'center', render: r => <span className="badge badge-navy">{r.totalItems || 0}</span>,
      total: rows => rows.reduce((s, r) => s + Number(r.totalItems || 0), 0)
    },
    {
      header: 'Weight (g)', key: 'totalWeight', align: 'right', render: r => <span className="text-mono">{fmt3(r.totalWeight)}</span>,
      total: rows => fmt3(rows.reduce((s, r) => s + Number(r.totalWeight || 0), 0))
    },
    {
      header: 'Purchase Amt', key: 'taxableAmount', align: 'right', render: r => <span>₹{fmt2(r.taxableAmount || (r.totalAmount ? r.totalAmount / 1.03 : 0))}</span>,
      total: rows => `₹${fmt2(rows.reduce((s, r) => s + Number(r.taxableAmount || (r.totalAmount ? r.totalAmount / 1.03 : 0)), 0))}`
    },
    {
      header: 'Purchase Amt (3%)', key: 'totalAmount', align: 'right', render: r => <strong style={{ color: '#1e40af' }}>₹{fmt2(r.totalAmount)}</strong>,
      total: rows => `₹${fmt2(rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0))}`
    },
    {
      header: 'Total COGS', key: 'totalCOGS', align: 'right', render: r => r.totalCOGS ? `₹${fmt2(r.totalCOGS)}` : <span className="text-muted">—</span>,
      total: rows => `₹${fmt2(rows.reduce((s, r) => s + Number(r.totalCOGS || 0), 0))}`
    },
    {
      header: 'Balance', key: 'balanceAmount', align: 'right', render: r => <span style={{ color: r.balanceAmount > 0 ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>₹{fmt2(r.balanceAmount)}</span>,
      total: rows => <span style={{ color: '#f87171' }}>₹{fmt2(rows.reduce((s, r) => s + Number(r.balanceAmount || 0), 0))}</span>
    },
    { header: 'Status', key: 'status', render: r => statusBadge(r.status) },
    {
      header: 'Actions', key: '_actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          {r.balanceAmount > 0 && (
            <button className="btn btn-ghost btn-sm btn-icon" title="Record Payment"
              style={{ color: 'var(--green)' }} onClick={() => openPayModal(r)}>
              <IndianRupee size={14} />
            </button>
          )}
          {r.paidAmount > 0 && (
            <button className="btn btn-ghost btn-sm btn-icon" title="Reverse Payment"
              style={{ color: '#d97706' }} onClick={() => openReverseModal(r)}>
              <RotateCcw size={14} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm btn-icon" title="Print all tags for this invoice"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => {
              const seqNum = (x) => { const m = String(x.skuNo || '').match(/(\d+)$/); return m ? parseInt(m[1], 10) : 0; };
              const rows = (itemRows || []).filter(x => x.purchaseID === r.purchaseID).sort((a, b) => seqNum(a) - seqNum(b));
              if (!rows.length) { toast.error('No items to print'); return; }
              printInvoiceTags(rows);
            }}>
            <Printer size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit Invoice" onClick={() => openEdit(r)}>
            <Edit2 size={14} />
          </button>
          <button
            className="btn btn-ghost btn-sm btn-icon"
            title="Delete Invoice"
            style={{ color: 'var(--red)' }}
            onClick={() => setConfirmDelete({ id: r.purchaseID, no: r.purchaseNo })}
          >
            <Trash2 size={14} />
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="flex-between mb-4" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--navy-900)' }}>Purchase Invoices</h1>
            <p className="text-muted" style={{ margin: '2px 0 0 0', fontSize: 13 }}>{view === 'invoices' ? `${data.length} invoices` : `${itemRows.length} item lines`}</p>
          </div>

          {/* Row-click preview (Item View only) */}
          {view === 'items' && previewRow && (
            <div style={{
              position: 'relative', width: 80, height: 80, flexShrink: 0,
              background: 'var(--silver-100)', border: '1px solid var(--silver-200)', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }} title={previewRow.skuNo || previewRow.itemName}>
              {previewLoading ? (
                <div className="spinner" />
              ) : previewImg ? (
                <img src={previewImg} alt={previewRow.skuNo || 'item'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 10, color: 'var(--silver-400)', textAlign: 'center', padding: '0 6px' }}>No image data</span>
              )}
              <button className="btn btn-ghost btn-sm btn-icon" onClick={closePreview} title="Close"
                style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,.85)', width: 18, height: 18, minWidth: 18, padding: 0 }}>
                <X size={11} />
              </button>
            </div>
          )}
        </div>

        {/* Top Action Buttons — Always visible on Desktop & Mobile */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', border: '1px solid var(--silver-300)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
            {[['invoices', 'Invoices'], ['items', 'Item View']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setView(k)}
                style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                  background: view === k ? 'var(--navy-900)' : '#fff',
                  color: view === k ? '#fff' : 'var(--silver-500)', transition: 'all .15s'
                }}>
                {label}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={printCalibrationStrip} title="Print a mm ruler to find your printer's exact label height and gap" style={{ background: '#fff', border: '1px solid var(--silver-300)', padding: '8px 14px' }}>
            <Printer size={15} /> Calibrate Printer
          </button>
          <button className="btn btn-amber" onClick={openModal} style={{ fontWeight: 700, padding: '8px 18px' }}><Plus size={16} /> New Invoice</button>
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '16px 20px' }}>
          {view === 'invoices' ? (
            <DataTable data={rowsWithSku} columns={columns} loading={loading}
              searchKeys={['purchaseNo', 'supplierName', 'status', 'supplierInvNo', '_skuHints']}
              emptyIcon={<ShoppingCart size={36} />}
              emptyText="No purchase invoices yet — create your first one"
              rowKey={r => r.purchaseID}
              onExpand={loadInvItems}
              expandedRender={invItemsPanel}
              exportName="purchase-invoices" />
          ) : (
            <div>
              {/* Single Top Search & Date Filter Bar */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, background: '#f8fafc', padding: '10px 16px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', flex: 1 }}>
                  <div className="search-box">
                    <Search size={15} />
                    <input
                      className="form-control"
                      placeholder="Search..."
                      value={globalSearch}
                      onChange={e => setGlobalSearch(e.target.value)}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', padding: '4px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                    <Calendar size={14} style={{ color: '#64748b' }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>DATE:</span>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={e => setFromDate(e.target.value)}
                      style={{ height: 30, fontSize: 12, padding: '0 6px', width: 125, background: '#fff' }}
                      title="From Date"
                    />
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>to</span>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={e => setToDate(e.target.value)}
                      style={{ height: 30, fontSize: 12, padding: '0 6px', width: 125, background: '#fff' }}
                      title="To Date"
                    />
                    {(fromDate || toDate) && (
                      <button
                        type="button"
                        onClick={() => { setFromDate(''); setToDate(''); }}
                        style={{ background: '#fee2e2', border: 'none', color: '#ef4444', borderRadius: 4, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 2 }}
                        title="Clear date filter"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Group By:</span>
                  <button type="button" className={`btn btn-sm ${itemGroupBy === 'vendor' ? 'btn-amber' : 'btn-ghost'}`} onClick={() => setItemGroupBy('vendor')} style={{ fontSize: 12 }}>
                    🏢 Company / Supplier
                  </button>
                  <button type="button" className={`btn btn-sm ${itemGroupBy === 'category' ? 'btn-amber' : 'btn-ghost'}`} onClick={() => setItemGroupBy('category')} style={{ fontSize: 12 }}>
                    📁 Category
                  </button>
                  <button type="button" className={`btn btn-sm ${itemGroupBy === 'none' ? 'btn-amber' : 'btn-ghost'}`} onClick={() => setItemGroupBy('none')} style={{ fontSize: 12 }}>
                    📋 All Items (Flat)
                  </button>
                </div>
              </div>

              {itemGroupBy !== 'none' && groupedItems ? (
                loading ? (
                  <div style={{ padding: 48, textAlign: 'center' }}>
                    <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto 12px' }} />
                    <div style={{ color: '#64748b', fontSize: 14, fontWeight: 500 }}>Data is loading...</div>
                    <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Please wait while we fetch your purchase items</div>
                  </div>
                ) : Object.keys(groupedItems).length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No purchase items found</div>
                ) : (
                  Object.entries(groupedItems).map(([groupName, groupList]) => {
                    const isCollapsed = collapsedCompanyGroups[groupName];
                    const groupQty = groupList.reduce((acc, x) => acc + Number(x.quantity || 0), 0);
                    const groupNet = groupList.reduce((acc, x) => acc + Number(x.netAmount || 0), 0);
                    const groupWt = groupList.reduce((acc, x) => acc + Number(x.weight || 0), 0);

                    return (
                      <div key={groupName} style={{ marginBottom: 16, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff', boxShadow: '0 2px 8px rgba(15,23,42,0.04)' }}>
                        {/* Group Header Banner */}
                        <div
                          onClick={() => setCollapsedCompanyGroups(p => ({ ...p, [groupName]: !p[groupName] }))}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '12px 18px', background: 'linear-gradient(90deg, #f1f5f9 0%, #ffffff 100%)',
                            cursor: 'pointer', borderBottom: isCollapsed ? 'none' : '1px solid #e2e8f0', userSelect: 'none'
                          }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ width: 28, height: 28, borderRadius: 8, background: '#0f172a', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                              {itemGroupBy === 'vendor' ? '🏢' : '📁'}
                            </div>
                            <div>
                              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                                {groupName}
                              </h4>
                            </div>
                            <span style={{ fontSize: 11.5, background: '#e0e7ff', color: '#3730a3', fontWeight: 700, padding: '2px 10px', borderRadius: 12, border: '1px solid #c7d2fe' }}>
                              {groupList.length} item{groupList.length > 1 ? 's' : ''}
                            </span>
                            <span style={{ fontSize: 12, color: '#475569', display: 'flex', gap: 10, alignItems: 'center' }}>
                              <span>Qty: <strong style={{ color: '#0f172a' }}>{groupQty}</strong></span> &bull;
                              <span>Wt: <strong style={{ color: '#0f172a' }}>{fmt3(groupWt)} g</strong></span> &bull;
                              <span>Net Amt: <strong style={{ color: '#0f766e' }}>₹{fmt2(groupNet)}</strong></span>
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s', color: '#64748b' }}>
                              <ChevronDown size={18} />
                            </div>
                          </div>
                        </div>

                        {/* Group Items Table — hide individual card search toolbar */}
                        {!isCollapsed && (
                          <div style={{ padding: '8px 12px' }}>
                            <DataTable
                              data={groupList}
                              columns={itemColumns}
                              loading={loading}
                              hideSearch={true}
                              pageSize={100000}
                              onRowClick={openPreview}
                              isRowSelected={r => previewRow && r.purchaseDetailID === previewRow.purchaseDetailID}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )
              ) : (
                <DataTable data={loading ? [] : filteredItemRows} columns={itemColumns} loading={loading}
                  hideSearch={true}
                  pageSize={20}
                  emptyIcon={<ShoppingCart size={36} />}
                  emptyText="No purchase items found"
                  onRowClick={openPreview}
                  isRowSelected={r => previewRow && r.purchaseDetailID === previewRow.purchaseDetailID} />
              )}
            </div>
          )}
        </div>
      </div>
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,28,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(12px)', padding: '2vh 2vw' }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '96vh', width: '98vw', maxWidth: '1880px', background: 'linear-gradient(160deg,#0f1a2e 0%,#0a1020 100%)', borderRadius: 20, boxShadow: '0 32px 80px rgba(0,0,0,0.8),0 0 0 1px rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg,#1a2744 0%,#0d1832 50%,#111827 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, padding: '18px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#f59e0b,#d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(245,158,11,0.3)' }}>
                  <Save size={20} color="#0d1832" />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em' }}>{editID ? 'Edit Purchase Invoice' : 'New Purchase Invoice'}</h3>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: '2px 0 0' }}>{editID ? `Editing PUR #${editID}` : 'Stock auto-posts on save \xb7 All fields in one view'}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {errs.length > 0 && <span style={{ fontSize: 11, color: '#fca5a5', display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', padding: '5px 12px', borderRadius: 20, fontWeight: 700 }}><AlertCircle size={12} /> {errs.length} error{errs.length > 1 ? 's' : ''}</span>}
                <button type="button" onClick={() => setModal(false)} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.6)' }} onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.color = '#ef4444'; }} onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}><X size={16} /></button>
              </div>
            </div>
            <form onSubmit={handleSave} noValidate style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 0', background: '#f0f4fa' }}>
                <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 12px rgba(15,23,42,0.06)', border: '1px solid rgba(203,213,225,0.5)' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ width: 3, height: 14, borderRadius: 4, background: '#f59e0b' }} />Invoice Header</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Purchase Date *</label><input type="date" max={todayStr()} value={header.purchaseDate} onChange={e => setHeader({ ...header, purchaseDate: e.target.value })} style={{ width: '100%', height: 38, boxSizing: 'border-box', padding: '0 12px', border: `1.5px solid ${submitted && !header.purchaseDate ? '#ef4444' : '#cbd5e1'}`, borderRadius: 8, fontSize: 13, outline: 'none' }} /></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Branch *</label>
                      <SearchableSelect
                        value={header.branchID}
                        onChange={val => setHeader({ ...header, branchID: val })}
                        options={branches.map(b => ({ value: b.branchID, label: b.branchName }))}
                        placeholder="— Branch —"
                        error={submitted && !header.branchID}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: 'span 2' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Supplier / Vendor *</label>
                        <button
                          type="button"
                          onClick={() => setQuickAddSup(true)}
                          title="Add New Supplier"
                          style={{ background: 'none', border: 'none', color: 'var(--primary-600)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0 2px' }}
                        >
                          <PlusCircle size={13} />
                        </button>
                      </div>
                      <SearchableSelect
                        value={header.supplierID}
                        onChange={val => setHeader({ ...header, supplierID: val })}
                        options={suppliers.map(s => ({ value: s.supplierID, label: `${s.supplierName}${s.mobile ? ` — ${s.mobile}` : ''}` }))}
                        placeholder="— Select Supplier —"
                        error={submitted && !header.supplierID}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Supp. Invoice No.</label><input placeholder="VEND/2025/001" value={header.supplierInvNo} onChange={e => setHeader({ ...header, supplierInvNo: e.target.value })} style={{ width: '100%', height: 38, boxSizing: 'border-box', padding: '0 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none' }} /></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Supp. Invoice Date</label><input type="date" value={header.supplierInvDate} onChange={e => setHeader({ ...header, supplierInvDate: e.target.value })} style={{ width: '100%', height: 38, boxSizing: 'border-box', padding: '0 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none' }} /></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: 'span 2' }}><label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Remarks</label><input placeholder="Optional notes" value={header.remarks} onChange={e => setHeader({ ...header, remarks: e.target.value })} style={{ width: '100%', height: 38, boxSizing: 'border-box', padding: '0 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none' }} /></div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><div style={{ width: 3, height: 18, borderRadius: 4, background: 'linear-gradient(#f59e0b,#d97706)' }} /><h4 style={{ margin: 0, color: '#0f172a', fontWeight: 800, fontSize: 15 }}>Items <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600, color: '#94a3b8', background: '#e2e8f0', padding: '2px 8px', borderRadius: 10 }}>{detailsLoading ? '…' : details.length}</span></h4></div>
                  <button type="button" onClick={addLine} disabled={detailsLoading} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 18px', background: detailsLoading ? '#e2e8f0' : 'linear-gradient(135deg,#f59e0b,#d97706)', border: 'none', borderRadius: 10, color: detailsLoading ? '#94a3b8' : '#1a0a00', fontWeight: 700, fontSize: 13, cursor: detailsLoading ? 'not-allowed' : 'pointer', boxShadow: detailsLoading ? 'none' : '0 4px 12px rgba(245,158,11,0.3)' }}><PlusCircle size={15} /> Add Item</button>
                </div>
                <div style={{ marginBottom: 20 }}>
                  {detailsLoading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 18, background: '#f8fafc', borderRadius: 14, border: '1.5px dashed #cbd5e1' }}>
                      <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid #e2e8f0', borderTopColor: '#f59e0b', animation: 'spin 0.9s linear infinite' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Loading Items…</div>
                        <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Fetching invoice details from database</div>
                      </div>
                    </div>
                  )}
                  {!detailsLoading && details.map((d, i) => {
                    const lineQty = Number(d.quantity) > 0 ? Number(d.quantity) : 1;
                    const lineTaxable = Number(d.weight || 0) * Number(d.rate || 0) * lineQty;
                    const mrp = Number(d.mrp || 0); const disc = Number(d.discount || 0);
                    const sp = Number(d.salePrice || 0) || (mrp > 0 ? mrp - disc : 0);
                    const cogs = Number(d.cogs || 0); const fixedExp = Number(d.fixedExpenses || 0); const otherExp = Number(d.otherExpenses || 0);
                    const autoSale = mrp > 0 ? Math.max(0, mrp - disc) : sp;
                    const baseAmt = autoSale > 0 ? autoSale / 1.03 : 0; const totalTax3 = autoSale - baseAmt;
                    // GST is charged on the sales Base amount (Sale Price ÷ 1.03), not the purchase Taxable
                    const lineCGST = d.isIGST ? 0 : baseAmt * Number(d.gstPercent || 0) / 2 / 100;
                    const lineIGST = d.isIGST ? baseAmt * Number(d.gstPercent || 0) / 100 : 0;
                    const lineTotal = lineTaxable + lineCGST * 2 + lineIGST;
                    // Gross Amount = Sale Price − COGS; profit % measured against Sale Price
                    const grossAmt = sp - cogs; const grossProfitPct = sp > 0 ? (grossAmt / sp) * 100 : 0;
                    const totExp = fixedExp + otherExp; const netAmt = grossAmt - totExp; const netProfitPct = sp > 0 ? (netAmt / sp) * 100 : 0;
                    const selectedItem = itemsMap.get(+d.itemID);
                    const categoryText = selectedItem ? `${selectedItem.itemName} (${selectedItem.purityCode})` : 'New Item';
                    const isColl = collapsedItems[i];
                    const inp = (border = '#e2e8f0', extra = {}) => ({ width: '100%', height: 34, boxSizing: 'border-box', padding: '0 10px', border: `1.5px solid ${border}`, borderRadius: 7, fontSize: 12.5, background: '#fff', color: '#0f172a', outline: 'none', ...extra });
                    const fld = (label, child) => <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}><span style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>{child}</div>;
                    return (
                      <div key={i} style={{ borderRadius: 14, background: '#fff', boxShadow: '0 2px 16px rgba(15,23,42,0.07)', marginBottom: 14, overflow: 'hidden', border: '1px solid rgba(203,213,225,0.6)' }}>
                        <div onClick={() => setCollapsedItems(p => ({ ...p, [i]: !p[i] }))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', background: isColl ? '#f8fafc' : 'linear-gradient(90deg,#f0f9ff 0%,#f8fafc 100%)', cursor: 'pointer', userSelect: 'none', borderBottom: isColl ? 'none' : '1px solid rgba(203,213,225,0.5)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <div style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(135deg,#1d4ed8,#1e3a8a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>{i + 1}</span></div>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{categoryText}</span>
                            {d.skuNo && <span style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: 11, padding: '2px 9px', borderRadius: 12, fontWeight: 700, fontFamily: 'var(--mono)', border: '1px solid #bfdbfe' }}>{d.skuNo}</span>}
                            {(d.isSavedInDB || d.purchaseDetailID) && <span style={{ background: '#f0fdf4', color: '#15803d', fontSize: 11, padding: '2px 9px', borderRadius: 12, fontWeight: 700, border: '1px solid #bbf7d0' }}>✓ Saved in DB</span>}
                            <span style={{ fontSize: 11.5, color: '#64748b', display: 'flex', gap: 8 }}>
                              <span>Qty: <strong style={{ color: '#0f172a' }}>{lineQty}</strong></span><span>&middot;</span>
                              <span>Wt: <strong style={{ color: '#0f172a' }}>{fmt3(d.weight || 0)} g</strong></span><span>&middot;</span>
                              <span style={{ background: '#f0fdf4', color: '#166534', padding: '1px 8px', borderRadius: 8, fontWeight: 700, border: '1px solid #bbf7d0' }}>&#8377;{fmt2(lineTotal)}</span>
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                            <button type="button" title="Print Tag" onClick={() => { const it = items.find(x => x.itemID === +d.itemID); printItemTag({ skuNo: d.skuNo, itemName: it?.itemName, weight: d.weight, quantity: d.quantity, price: d.mrp, purityCode: it?.purityCode }); }} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}><Printer size={14} /></button>
                            {details.length > 1 && <button type="button" onClick={() => removeLine(i)} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #fee2e2', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>}
                            <button type="button" title={isColl ? 'Expand' : 'Collapse'} onClick={() => setCollapsedItems(p => ({ ...p, [i]: !p[i] }))} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', transform: isColl ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }}><ChevronDown size={14} /></button>
                          </div>
                        </div>
                        {!isColl && (
                          <div style={{ padding: '16px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 12 }}>

                            {/* Single Row: All fields in one line */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', paddingBottom: 6 }}>

                              <div style={{ width: 44, height: 44, border: '1px dashed #cbd5e1', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', flexShrink: 0 }}>
                                <ImageUploadCell preview={d.imagePreview} onChange={e => e.target.files?.[0] && handleImageChange(i, e.target.files[0])} onClear={() => clearImage(i)} />
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category *</span>
                                  <button
                                    type="button"
                                    onClick={() => setQuickAddItem(i)}
                                    title="Add New Category/Item"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: 'var(--primary-600)',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '0 2px',
                                      marginLeft: 6
                                    }}
                                  >
                                    <PlusCircle size={13} />
                                  </button>
                                </div>
                                <div style={{ width: 180 }}>
                                  <SearchableSelect
                                    compact
                                    value={d.itemID}
                                    onChange={val => onItemSelect(i, val)}
                                    options={items.map(it => ({ value: it.itemID, label: `${it.itemName} (${it.purityCode})` }))}
                                    placeholder="— Category —"
                                    error={submitted && !d.itemID}
                                  />
                                </div>
                              </div>

                              {fld('SKU No', <input style={inp('#cbd5e1', { width: 125 })} placeholder="SKU" value={d.skuNo} onChange={e => updateLine(i, 'skuNo', e.target.value)} />)}
                              {fld('Qty', <input type="number" min="1" step="1" style={inp('#cbd5e1', { textAlign: 'center', fontWeight: 600, width: 50 })} placeholder="1" value={d.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />)}
                              {fld('Collection', (
                                <div style={{ width: 108 }}>
                                  <SearchableSelect
                                    compact
                                    value={d.collection || ''}
                                    onChange={val => updateLine(i, 'collection', val)}
                                    options={(() => {
                                      const opts = masterCollections.map(cm => ({ value: cm.collectionName, label: cm.collectionName }));
                                      // Keep showing values saved before the master existed (or with old casing)
                                      if (d.collection && !opts.some(o => o.value === d.collection)) opts.unshift({ value: d.collection, label: d.collection });
                                      return opts;
                                    })()}
                                    placeholder="Collection"
                                  />
                                </div>
                              ))}
                              {fld('Size', <input type="text" maxLength={50} style={inp('#cbd5e1', { width: 62 })} placeholder="Size" value={d.productSize || ''} onChange={e => updateLine(i, 'productSize', e.target.value)} />)}
                              {fld('Vnd.No.', <input style={inp('#cbd5e1', { width: 65 })} placeholder="SR No." value={d.vendorSRNo} onChange={e => updateLine(i, 'vendorSRNo', e.target.value)} />)}
                              {fld('GS.wt.', <input type="number" min="0" step="0.001" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 60 })} placeholder="0.000" value={d.grossWeight} onChange={e => updateLine(i, 'grossWeight', e.target.value)} />)}
                              {fld('Net Wt *', <input type="number" min="0" step="0.001" style={inp(submitted && (!d.weight || +d.weight <= 0) ? '#ef4444' : '#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', fontWeight: 700, width: 60 })} placeholder="0.000" value={d.weight} onChange={e => updateLine(i, 'weight', e.target.value)} />)}
                              {fld('Labour/g', <input type="number" min="0" step="0.01" style={inp(submitted && (!d.rate || +d.rate <= 0) ? '#ef4444' : '#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 70 })} placeholder="0.00" value={d.labourPerGram} onChange={e => autoCalcRate(i, 'labourPerGram', e.target.value)} />)}
                              {fld('Silver/g', <input type="number" min="0" step="0.01" style={inp(submitted && (!d.rate || +d.rate <= 0) ? '#ef4444' : '#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 70 })} placeholder="0.00" value={d.silverRatePerGram} onChange={e => autoCalcRate(i, 'silverRatePerGram', e.target.value)} />)}
                              {fld('COGS', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 68 })} placeholder="0.00" value={d.cogs} onChange={e => updateLine(i, 'cogs', e.target.value)} />)}
                              {fld('MRP', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 68 })} placeholder="0.00" value={d.mrp} onChange={e => { const v = e.target.value; const s = Number(v) - Number(d.discount || 0); setDetails(p => p.map((r, j) => j === i ? { ...r, mrp: v, salePrice: s > 0 ? s.toFixed(2) : '' } : r)); }} />)}
                              {fld('CmbPrice', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 68 })} placeholder="N/A" value={d.setComboPrice} onChange={e => updateLine(i, 'setComboPrice', e.target.value)} />)}
                              {fld('Discount', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 70 })} placeholder="0.00" value={d.discount} onChange={e => { const v = e.target.value; const s = Number(d.mrp || 0) - Number(v); setDetails(p => p.map((r, j) => j === i ? { ...r, discount: v, salePrice: s > 0 ? s.toFixed(2) : '' } : r)); }} />)}
                              {fld('Sale Price', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', fontWeight: 700, width: 68 })} placeholder="0.00" value={d.salePrice} onChange={e => updateLine(i, 'salePrice', e.target.value)} />)}
                              {fld('Fixed Exp', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 62 })} placeholder="0.00" value={d.fixedExpenses} onChange={e => updateLine(i, 'fixedExpenses', e.target.value)} />)}
                              {fld('Oth.Exp.', <input type="number" min="0" step="0.01" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 62 })} placeholder="0.00" value={d.otherExpenses} onChange={e => updateLine(i, 'otherExpenses', e.target.value)} />)}
                              {fld('GST %', <input type="number" min="0" step="0.5" style={inp('#cbd5e1', { fontFamily: 'var(--mono)', textAlign: 'right', width: 55 })} placeholder="3" value={d.gstPercent} onChange={e => updateLine(i, 'gstPercent', e.target.value)} />)}
                              {fld('IGST', (
                                <div
                                  onClick={() => updateLine(i, 'isIGST', !d.isIGST)}
                                  style={{
                                    height: 32, minHeight: 32, width: 38, display: 'inline-flex', alignItems: 'center',
                                    justifyContent: 'center', padding: 0,
                                    background: d.isIGST ? '#eff6ff' : '#fff',
                                    border: `1.5px solid ${d.isIGST ? '#93c5fd' : '#cbd5e1'}`,
                                    borderRadius: 6, cursor: 'pointer', userSelect: 'none', boxSizing: 'border-box'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={d.isIGST || false}
                                    onChange={e => updateLine(i, 'isIGST', e.target.checked)}
                                    onClick={e => e.stopPropagation()}
                                    style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                                  />
                                </div>
                              ))}
                              {fld('Description', <input style={inp('#cbd5e1', { width: 120 })} placeholder="Notes / description" value={d.description || ''} onChange={e => updateLine(i, 'description', e.target.value)} />)}

                            </div>

                            {/* Line 3: Calculations Summary Bar */}
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 16px',
                              background: '#fff',
                              borderRadius: '8px',
                              marginTop: 6,
                              border: '1px solid #cbd5e1',
                              gap: 12
                            }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', fontSize: 12 }}>
                                <span style={{ color: '#475569', fontWeight: 700, letterSpacing: '0.03em' }}>PURCHASE:</span>
                                {/* Purchase = (Labour + Silver)/g × Net Wt (per piece); Purchase(3%) adds 3% */}
                                <span style={{ color: '#64748b' }}>Purchase: <strong style={{ fontFamily: 'var(--mono)', color: '#0f172a' }}>₹{fmt2(Number(d.rate || 0) * Number(d.weight || 0))}</strong></span>
                                <span style={{ color: '#e2e8f0' }}>|</span>
                                <span style={{ color: '#64748b' }}>Purchase(3%): <strong style={{ fontFamily: 'var(--mono)', color: '#0f172a' }}>₹{fmt2(Number(d.rate || 0) * Number(d.weight || 0) * 1.03)}</strong></span>
                                <span style={{ color: '#e2e8f0' }}>|</span>
                                <span style={{ color: '#64748b' }}>
                                  GST ({d.gstPercent || 0}%):{' '}
                                  <strong style={{ fontFamily: 'var(--mono)', color: '#0f172a' }}>
                                    {d.isIGST ? `₹${fmt2(lineIGST)} (IGST)` : `₹${fmt2(lineCGST * 2)} (CGST+SGST)`}
                                  </strong>
                                </span>
                                <span style={{ color: '#e2e8f0' }}>|</span>
                                {/* Gross Amount = Sale Price − COGS */}
                                <span style={{ color: '#0f172a', fontWeight: 700 }}>
                                  Gross Amount: <strong style={{ fontFamily: 'var(--mono)', color: '#1d4ed8', fontSize: 13, fontWeight: 800 }}>₹{fmt2(sp - cogs)}</strong>
                                </span>
                              </div>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', fontSize: 12 }}>
                                <span style={{ color: '#64748b' }}>Base: <strong style={{ fontFamily: 'var(--mono)', color: '#0f172a' }}>₹{fmt2(baseAmt)}</strong></span>
                                <span style={{ color: '#e2e8f0' }}>|</span>
                                <span style={{
                                  color: grossAmt >= 0 ? '#166534' : '#991b1b',
                                  background: grossAmt >= 0 ? '#f0fdf4' : '#fef2f2',
                                  padding: '2px 8px',
                                  borderRadius: 6,
                                  fontWeight: 700
                                }}>
                                  GP: <span style={{ fontFamily: 'var(--mono)' }}>₹{fmt2(grossAmt)} ({grossProfitPct.toFixed(1)}%)</span>
                                </span>
                                <span style={{
                                  color: netAmt >= 0 ? '#0f766e' : '#991b1b',
                                  background: netAmt >= 0 ? '#f0fdfa' : '#fef2f2',
                                  padding: '2px 8px',
                                  borderRadius: 6,
                                  fontWeight: 700
                                }}>
                                  NP: <span style={{ fontFamily: 'var(--mono)' }}>₹{fmt2(netAmt)} ({netProfitPct.toFixed(1)}%)</span>
                                </span>
                              </div>
                            </div>

                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ background: 'linear-gradient(90deg,#0f172a 0%,#1e293b 100%)', borderRadius: 12, padding: '16px 22px', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}><span style={{ background: 'rgba(245,158,11,0.2)', color: '#f59e0b', padding: '3px 10px', borderRadius: 8, fontWeight: 800, fontSize: 12 }}>{details.length} item{details.length !== 1 ? 's' : ''}</span>Net Weight: <span style={{ color: '#fff', fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700 }}>{fmt3(totals.totalWeight)} g</span></div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Taxable: <span style={{ color: '#fff', fontWeight: 700, fontFamily: 'var(--mono)' }}>&#8377;{fmt2(totals.taxable)}</span></span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>CGST: <span style={{ color: '#6ee7f7', fontWeight: 700, fontFamily: 'var(--mono)' }}>&#8377;{fmt2(totals.cgst)}</span></span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>SGST: <span style={{ color: '#6ee7f7', fontWeight: 700, fontFamily: 'var(--mono)' }}>&#8377;{fmt2(totals.sgst)}</span></span>
                      {totals.igst > 0 && <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>IGST: <span style={{ color: '#6ee7f7', fontWeight: 700, fontFamily: 'var(--mono)' }}>&#8377;{fmt2(totals.igst)}</span></span>}
                      <div style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', padding: '6px 18px', borderRadius: 10, fontFamily: 'var(--mono)', color: '#1a0a00', fontWeight: 800, fontSize: 18, whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(245,158,11,0.35)' }}>&#8377;{fmt2(totals.total)}</div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, alignItems: 'center', padding: '10px 0 4px', borderTop: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#94a3b8', fontSize: 10.5 }}>💡 Net g per piece · Purch. Price = (Labour + Silver/g) × Net g × Qty</span>
                </div>
              </div>
              <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', flexShrink: 0, padding: '16px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 -4px 20px rgba(15,23,42,0.06)' }}>
                <div style={{ fontSize: 14, color: '#64748b' }}>Grand Total:&nbsp;<strong style={{ color: '#0f172a', fontSize: 20, fontFamily: 'var(--mono)', fontWeight: 800 }}>&#8377;{fmt2(totals.total)}</strong><span style={{ color: '#94a3b8', margin: '0 8px' }}>&middot;</span>Weight: <strong style={{ color: '#0f172a' }}>{fmt3(totals.totalWeight)} g</strong></div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" onClick={() => setModal(false)} style={{ padding: '9px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 24px', minWidth: 160, justifyContent: 'center', background: saving ? '#fde68a' : 'linear-gradient(135deg,#f59e0b,#d97706)', border: 'none', borderRadius: 10, color: '#1a0a00', fontWeight: 800, fontSize: 13.5, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 16px rgba(245,158,11,0.35)' }}>
                    {saving ? <><span className="spinner" style={{ width: 15, height: 15, borderTopColor: '#92400e' }} /> Saving\u2026</> : <><Save size={15} /> {editID ? 'Update Invoice' : 'Create Invoice'}</>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* â”€â”€ Confirm Delete Dialog â”€â”€ */}
      {/* ── Record Payment Dialog ── */}
      {payModal && (
        <div className="modal-overlay" onClick={() => !paying && setPayModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IndianRupee size={18} color="var(--green)" />
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>Record Payment</h3>
                  <p style={{ fontSize: 12, color: 'var(--silver-500)', margin: 0 }}>
                    {payModal.purchaseNo} · {payModal.supplierName}
                  </p>
                </div>
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setPayModal(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handlePayment}>
              <div className="modal-body" style={{ paddingTop: 16 }}>
                {/* Invoice summary */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[['Total', payModal.totalAmount, 'var(--navy-800)'],
                  ['Paid', payModal.paidAmount, 'var(--green)'],
                  ['Balance', payModal.balanceAmount, 'var(--red)']].map(([lbl, val, clr]) => (
                    <div key={lbl} style={{ flex: 1, background: 'var(--silver-100)', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--silver-500)', letterSpacing: '.05em' }}>{lbl}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 14, color: clr }}>₹{fmt2(val)}</div>
                    </div>
                  ))}
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Amount (₹) *</label>
                  <input className="form-control" type="number" min="0.01" step="0.01" autoFocus
                    value={payForm.amount}
                    onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                    style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => setPayForm({ ...payForm, amount: payModal.balanceAmount })}>
                      Full Balance (₹{fmt2(payModal.balanceAmount)})
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => setPayForm({ ...payForm, amount: (Number(payModal.balanceAmount) / 2).toFixed(2) })}>
                      Half
                    </button>
                  </div>
                </div>
                <div className="grid-2" style={{ gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Payment Mode</label>
                    <SearchableSelect
                      value={payForm.paymentMode}
                      onChange={val => setPayForm({ ...payForm, paymentMode: val })}
                      options={['CASH', 'UPI', 'BANK', 'CHEQUE', 'CARD'].map(m => ({ value: m, label: m }))}
                      placeholder="Payment Mode"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Remarks</label>
                    <input className="form-control" placeholder="Optional" value={payForm.remarks}
                      onChange={e => setPayForm({ ...payForm, remarks: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid var(--silver-200)' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setPayModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-amber" disabled={paying} style={{ minWidth: 150, justifyContent: 'center' }}>
                  {paying
                    ? <><span className="spinner" style={{ width: 15, height: 15, borderTopColor: 'var(--navy-900)' }} /> Saving...</>
                    : <><IndianRupee size={15} /> Record Payment</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Reverse Payment Dialog ── */}
      {reverseModal && (
        <div className="modal-overlay" onClick={() => !reversing && setReverseModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RotateCcw size={18} color="#d97706" />
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>Reverse Payment</h3>
                  <p style={{ fontSize: 12, color: 'var(--silver-500)', margin: 0 }}>
                    {reverseModal.purchaseNo} · {reverseModal.supplierName}
                  </p>
                </div>
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setReverseModal(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handleReverse}>
              <div className="modal-body" style={{ paddingTop: 16 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[['Total', reverseModal.totalAmount, 'var(--navy-800)'],
                  ['Paid', reverseModal.paidAmount, 'var(--green)'],
                  ['Balance', reverseModal.balanceAmount, 'var(--red)']].map(([lbl, val, clr]) => (
                    <div key={lbl} style={{ flex: 1, background: 'var(--silver-100)', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--silver-500)', letterSpacing: '.05em' }}>{lbl}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 14, color: clr }}>₹{fmt2(val)}</div>
                    </div>
                  ))}
                </div>
                <div className="form-group">
                  <label className="form-label">Reversal Amount (₹) *</label>
                  <input className="form-control" type="number" min="0.01" step="0.01" autoFocus
                    value={reverseForm.amount}
                    onChange={e => setReverseForm({ ...reverseForm, amount: e.target.value })}
                    style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => setReverseForm({ ...reverseForm, amount: reverseModal.paidAmount })}>
                      Full Paid (₹{fmt2(reverseModal.paidAmount)})
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Reason</label>
                  <input className="form-control" placeholder="e.g. Entered by mistake" value={reverseForm.reason}
                    onChange={e => setReverseForm({ ...reverseForm, reason: e.target.value })} />
                </div>
                <p style={{ fontSize: 12, color: '#d97706', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                  <AlertCircle size={13} />
                  Paid amount will reduce and status will go back to PARTIAL / UNPAID.
                </p>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid var(--silver-200)' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setReverseModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-amber" disabled={reversing} style={{ minWidth: 160, justifyContent: 'center' }}>
                  {reversing
                    ? <><span className="spinner" style={{ width: 15, height: 15, borderTopColor: 'var(--navy-900)' }} /> Reversing...</>
                    : <><RotateCcw size={15} /> Reverse Payment</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Delete Dialog ── */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Trash2 size={18} color="var(--red)" />
                </div>
                <h3 style={{ margin: 0, color: 'var(--navy-900)' }}>Delete Invoice</h3>
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setConfirmDelete(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ paddingTop: 20, paddingBottom: 20 }}>
              <p style={{ fontSize: 15, color: 'var(--navy-700)', lineHeight: 1.6 }}>
                Are you sure you want to delete invoice{' '}
                <strong style={{ color: 'var(--navy-900)' }}>{confirmDelete.no}</strong>?
              </p>
              <p style={{ fontSize: 13, color: 'var(--red)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={14} />
                This will also reverse the stock entries. This cannot be undone.
              </p>
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid var(--silver-200)' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDelete}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={15} /> Delete Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Add Supplier Dialog ── */}
      {quickAddSup && (
        <div className="modal-overlay" style={{ zIndex: 999999 }} onClick={e => e.target === e.currentTarget && setQuickAddSup(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <h3 style={{ margin: 0, color: 'var(--navy-900)' }}>Add Supplier</h3>
              <button type="button" className="btn btn-ghost btn-icon" onClick={() => setQuickAddSup(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleQuickSupSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20, paddingBottom: 20 }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 600 }}>Supplier Name *</label>
                  <input
                    className="form-control"
                    placeholder="e.g. Mahavir Silver Co."
                    value={quickSupForm.supplierName}
                    onChange={e => setQuickSupForm({ ...quickSupForm, supplierName: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>Mobile</label>
                    <input className="form-control" placeholder="98XXXXXXXX" value={quickSupForm.mobile}
                      onChange={e => setQuickSupForm({ ...quickSupForm, mobile: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>City</label>
                    <input className="form-control" placeholder="e.g. Rajkot" value={quickSupForm.city}
                      onChange={e => setQuickSupForm({ ...quickSupForm, city: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 600 }}>GST Number</label>
                  <input className="form-control" placeholder="24XXXXX0000X1Z5" value={quickSupForm.gstNumber}
                    onChange={e => setQuickSupForm({ ...quickSupForm, gstNumber: e.target.value })} />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--silver-500)' }}>
                  Full details (address, bank, credit days) can be added later from the Suppliers page.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setQuickAddSup(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={quickSupSaving}>
                  {quickSupSaving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />} Add Supplier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Quick Add Category Dialog ── */}
      {quickAddItem !== null && (
        <div className="modal-overlay" style={{ zIndex: 999999 }} onClick={e => e.target === e.currentTarget && setQuickAddItem(null)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <h3 style={{ margin: 0, color: 'var(--navy-900)' }}>Quick Add Category</h3>
              <button type="button" className="btn btn-ghost btn-icon" onClick={() => setQuickAddItem(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handleQuickAddSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20, paddingBottom: 20 }}>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 600 }}>Item Name *</label>
                  <input
                    className="form-control"
                    placeholder="e.g. Silver Ring 92.5, Silver Chain"
                    value={quickAddForm.itemName}
                    onChange={e => setQuickAddForm({ ...quickAddForm, itemName: e.target.value })}
                    required
                    autoFocus
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>Category Group *</label>
                    <SearchableSelect
                      value={quickAddForm.categoryID}
                      onChange={val => setQuickAddForm({ ...quickAddForm, categoryID: val })}
                      options={masterCategories.map(c => ({ value: c.categoryID, label: c.categoryName }))}
                      placeholder="Select Category Group"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>Purity *</label>
                    <SearchableSelect
                      value={quickAddForm.purityID}
                      onChange={val => setQuickAddForm({ ...quickAddForm, purityID: val })}
                      options={masterPurities.map(p => ({ value: p.purityID, label: p.purityCode }))}
                      placeholder="Select Purity"
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>HSN Code</label>
                    <input
                      className="form-control"
                      value={quickAddForm.hsnCode}
                      onChange={e => setQuickAddForm({ ...quickAddForm, hsnCode: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>GST %</label>
                    <input
                      type="number"
                      className="form-control"
                      value={quickAddForm.gstPercent}
                      onChange={e => setQuickAddForm({ ...quickAddForm, gstPercent: +e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>Unit</label>
                    <select
                      className="form-control"
                      value={quickAddForm.unit}
                      onChange={e => setQuickAddForm({ ...quickAddForm, unit: e.target.value })}
                      style={{ height: 34 }}
                    >
                      <option value="GMS">GMS</option>
                      <option value="PCS">PCS</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 600 }}>Item Short Code</label>
                  <input
                    className="form-control"
                    placeholder="e.g. SC, RC, BR (short identifier)"
                    value={quickAddForm.shortCode || ''}
                    onChange={e => setQuickAddForm({ ...quickAddForm, shortCode: e.target.value })}
                  />
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>Optional short code for quick reference (e.g. SC for Silver Coin).</span>
                </div>

                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!quickAddForm.showSKUSize}
                      onChange={e => setQuickAddForm({ ...quickAddForm, showSKUSize: e.target.checked })}
                    />
                    <span>Show SKU Size</span>
                  </label>
                  <span className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginLeft: 24, marginTop: 2 }}>
                    When enabled, the item's size is shown along with the SKU.
                  </span>
                </div>

              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid var(--silver-200)' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setQuickAddItem(null)}>Cancel</button>
                <button type="submit" className="btn btn-amber" disabled={quickAddSaving}>
                  {quickAddSaving ? 'Saving...' : 'Create Category/Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ── Item Detail Modal (Full Details on click in Item View) ── */}
      {itemDetailModal && previewRow && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(8,12,28,0.75)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(10px)', padding: 20
        }} onClick={closePreview}>
          <div style={{
            width: '100%', maxWidth: 780, maxHeight: '90vh', background: '#fff',
            borderRadius: 16, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              padding: '18px 24px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', color: '#fff'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff' }}>
                    {previewRow.itemName || 'Item Details'}
                  </h3>
                  {previewRow.skuNo && (
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700,
                      color: '#a5b4fc', background: 'rgba(99,102,241,0.2)',
                      border: '1px solid rgba(165,180,252,0.3)', padding: '2px 8px', borderRadius: 6
                    }}>
                      {previewRow.skuNo}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                  Invoice No: <strong style={{ color: '#cbd5e1' }}>{previewRow.purchaseNo}</strong> &bull; Date: {new Date(previewRow.purchaseDate).toLocaleDateString('en-IN')}
                </div>
              </div>
              <button onClick={closePreview} style={{
                background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#fff'
              }}>
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Top Banner: Image + Key Metrics */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                {/* Image */}
                <div style={{
                  width: 130, height: 130, borderRadius: 10, background: '#fff',
                  border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
                  position: 'relative'
                }}>
                  {previewLoading ? (
                    <div className="spinner" />
                  ) : previewImg ? (
                    <div
                      onClick={() => setBigImage({ url: previewImg, title: previewRow?.itemName || 'Item Photo' })}
                      style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}
                      title="Click to view full screen image"
                    >
                      <img src={previewImg} alt={previewRow.itemName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div
                        style={{
                          position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff',
                          opacity: 0, transition: 'opacity 0.2s', gap: 4
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                      >
                        <Maximize2 size={24} />
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Enlarge</span>
                      </div>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: 8 }}>No photo data</span>
                  )}
                </div>

                {/* Quick Info Grid */}
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Supplier / Vendor</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{previewRow.vendor || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Category</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{previewRow.category || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Purity</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{previewRow.purityCode || '925'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Quantity</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{Number(previewRow.quantity)} pcs</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Product Status</div>
                    <div style={{ marginTop: 2 }}>{statusBadge(previewRow.productStatus || 'IN STOCK')}</div>
                  </div>
                </div>
              </div>

              {/* Tag Sticker Preview Card */}
              <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: 12, padding: '14px 18px', color: '#fff',
                boxShadow: '0 4px 15px rgba(15,23,42,0.15)',
                display: 'flex', flexDirection: 'column', gap: 10
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>🏷️</span> PRINTED TAG STICKER DETAILS
                  </div>
                  <button className="btn btn-amber btn-sm"
                    style={{ fontSize: 11.5, padding: '3px 10px', height: 26 }}
                    onClick={() => printItemTag({ ...previewRow, price: previewRow.salePrice || previewRow.mrp })}>
                    <Printer size={13} /> Print Tag Sticker Now
                  </button>
                </div>

                {/* Simulated Physical Tag Banner */}
                <div style={{
                  background: '#ffffff', color: '#000', borderRadius: 8, padding: '10px 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                  border: '2px dashed #cbd5e1', boxShadow: 'inset 0 0 0 1px #e2e8f0'
                }}>
                  {/* Tag Brand Logo */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 65, height: 36, flexShrink: 0 }}>
                    <img src="/cq-mark-only.png" alt="Carat Quest" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>

                  <div style={{ width: 1, height: 36, background: '#cbd5e1', flexShrink: 0 }} />

                  {/* Tag Values */}
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 8.5, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>TAG SKU NO</div>
                      <div style={{ fontSize: 12.5, fontWeight: 900, fontFamily: 'var(--mono)', color: '#3730a3' }}>
                        {previewRow.skuNo || '—'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 8.5, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>NET WEIGHT</div>
                      <div style={{ fontSize: 12.5, fontWeight: 900, color: '#047857' }}>
                        {fmt3(previewRow.weight || previewLineDetail?.weight)} g
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 8.5, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>METAL & PURITY</div>
                      <div style={{ fontSize: 12.5, fontWeight: 900, color: '#1e293b' }}>
                        {previewRow.purityCode || '925'} SILVER
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 8.5, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>TAG PRICE</div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: '#be185d' }}>
                        ₹{fmt2(previewRow.salePrice || previewRow.mrp)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Technical & Financial Breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>

                {/* Specs Card */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                    📦 Specifications & Weight
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Collection:</span>
                      <strong style={{ color: '#1e293b' }}>{previewLineDetail?.collection || '—'}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Product Size:</span>
                      <strong style={{ color: '#1e293b' }}>{previewLineDetail?.productSize || '—'}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Vendor SR No:</span>
                      <strong style={{ color: '#1e293b' }}>{previewLineDetail?.vendorSRNo || '—'}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Gross Weight:</span>
                      <strong style={{ color: '#1e293b' }}>{fmt3(previewLineDetail?.grossWeight)} g</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Net Weight:</span>
                      <strong style={{ color: '#0f766e', fontWeight: 700 }}>{fmt3(previewRow.weight || previewLineDetail?.weight)} g</strong>
                    </div>
                  </div>
                </div>

                {/* Purchase Cost Card */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                    💰 Cost & Purchase Rate
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Rate / Gram:</span>
                      <strong style={{ color: '#1e293b' }}>₹{fmt2(previewRow.rate || previewLineDetail?.rate)}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Labour / Gram:</span>
                      <strong style={{ color: '#1e293b' }}>₹{fmt2(previewLineDetail?.labourPerGram)}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Silver Rate / Gram:</span>
                      <strong style={{ color: '#1e293b' }}>₹{fmt2(previewLineDetail?.silverRatePerGram)}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Purchase Price:</span>
                      <strong style={{ color: '#1e293b' }}>₹{fmt2(previewRow.purchasePrice)}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Total COGS:</span>
                      <strong style={{ color: '#4338ca', fontWeight: 700 }}>₹{fmt2(previewLineDetail?.cogs || previewRow.purchasePrice)}</strong>
                    </div>
                  </div>
                </div>

                {/* Selling & Margin Card */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                    🏷️ Sale Price & Margins
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>MRP:</span>
                      <strong style={{ color: '#1e293b' }}>₹{fmt2(previewRow.mrp)}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Discount:</span>
                      <strong style={{ color: previewRow.discount > 0 ? '#ef4444' : '#1e293b' }}>
                        {previewRow.discount > 0 ? `- ₹${fmt2(previewRow.discount)}` : '—'}
                      </strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Selling Price:</span>
                      <strong style={{ color: '#be185d', fontWeight: 700 }}>₹{fmt2(previewRow.salePrice)}</strong>
                    </div>
                    <div className="flex-between">
                      <span style={{ color: '#64748b' }}>Total Expenses:</span>
                      <strong style={{ color: '#7c3aed' }}>₹{fmt2(previewRow.totalExpenses)}</strong>
                    </div>
                    <div className="flex-between" style={{ paddingTop: 4, borderTop: '1px dashed #e2e8f0' }}>
                      <span style={{ color: '#0f172a', fontWeight: 700 }}>Net Profit %:</span>
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 8,
                        color: previewRow.netProfitPct > 0 ? '#0f766e' : previewRow.netProfitPct < 0 ? '#b91c1c' : '#64748b',
                        background: previewRow.netProfitPct > 0 ? '#f0fdfa' : previewRow.netProfitPct < 0 ? '#fef2f2' : '#f1f5f9',
                      }}>
                        {Number(previewRow.netProfitPct || 0).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>

              </div>

            </div>

            {/* Modal Footer */}
            <div style={{
              background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '12px 24px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm"
                  onClick={() => printItemTag({ ...previewRow, price: previewRow.salePrice || previewRow.mrp })}>
                  <Printer size={14} /> Print Tag Sticker
                </button>
                <button className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const row = data.find(x => x.purchaseID === previewRow.purchaseID);
                    closePreview();
                    if (row) openEdit(row);
                  }}>
                  <Edit2 size={14} /> Edit Invoice
                </button>
              </div>
              <button className="btn btn-navy btn-sm" onClick={closePreview}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* The publish popup, shared with Website CMS. It reads the piece's website
          details, its pictures and its tag photograph for itself, so both screens
          show the same thing and a change to it is made once. */}
      {webModalRow && (
        <PublishItemModal
          piece={webModalRow}
          onClose={() => setWebModalRow(null)}
          onSaved={(saved) => {
            // The row's globe turns green off this flag, so the list is told
            // rather than refetched
            setItemRows(prev => prev.map(x => (
              x.purchaseDetailID === webModalRow.purchaseDetailID
                ? { ...x, showOnWebsite: saved.showOnWebsite }
                : x
            )));
            if (previewRow?.purchaseDetailID === webModalRow.purchaseDetailID) {
              setPreviewRow(prev => (prev ? { ...prev, showOnWebsite: saved.showOnWebsite } : null));
            }
          }}
        />
      )}

      {/* Full Screen Image Lightbox Modal */}
      {bigImage && (
        <div
          onClick={() => setBigImage(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(15, 23, 42, 0.94)', backdropFilter: 'blur(10px)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 20
          }}
        >
          {/* Header Controls */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 1050, display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', color: '#fff', marginBottom: 14
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Maximize2 size={20} style={{ color: '#6366f1' }} />
              <span>{bigImage.title || 'Item High Resolution Photo'}</span>
              {filteredItemRows && filteredItemRows.length > 0 && bigImage.index !== undefined && (
                <span style={{
                  fontSize: 12, background: 'rgba(99,102,241,0.25)', color: '#a5b4fc',
                  border: '1px solid rgba(99,102,241,0.4)', padding: '2px 10px', borderRadius: 12,
                  fontWeight: 600
                }}>
                  Item {bigImage.index + 1} of {filteredItemRows.length}
                </span>
              )}
            </div>
            <button
              onClick={() => setBigImage(null)}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                borderRadius: '50%', width: 40, height: 40, display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              title="Close (Esc)"
            >
              <X size={24} />
            </button>
          </div>

          {/* Center Area with Navigation Arrows & Image */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: 1150 }}>
            {/* Left Arrow Button (<) */}
            {filteredItemRows && filteredItemRows.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleNavBigImage(-1); }}
                style={{
                  position: 'absolute', left: 10, zIndex: 100000,
                  background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
                  borderRadius: '50%', width: 54, height: 54, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)', transition: 'all 0.2s ease',
                  backdropFilter: 'blur(4px)'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.8)'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.transform = 'scale(1)'; }}
                title="Previous Product (←)"
              >
                <ChevronLeft size={34} />
              </button>
            )}

            {/* Full Screen Image Container */}
            <div
              onClick={e => e.stopPropagation()}
              style={{
                maxHeight: '82vh', maxWidth: '85vw', minWidth: 320, minHeight: 320, borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)', background: '#0f172a',
                border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 12
              }}
            >
              {bigImage.loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 60, color: '#94a3b8' }}>
                  <Loader2 size={36} style={{ animation: 'imgSpin 1s linear infinite', color: '#6366f1' }} />
                  <span style={{ fontSize: 14 }}>Loading product photo...</span>
                </div>
              ) : bigImage.url ? (
                <img
                  src={bigImage.url}
                  alt={bigImage.title}
                  style={{ maxHeight: '78vh', maxWidth: '82vw', objectFit: 'contain', display: 'block', borderRadius: 8 }}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 60, color: '#94a3b8' }}>
                  <Camera size={44} style={{ color: '#475569' }} />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>No high-resolution photo for this item</span>
                </div>
              )}
            </div>

            {/* Right Arrow Button (>) */}
            {filteredItemRows && filteredItemRows.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleNavBigImage(1); }}
                style={{
                  position: 'absolute', right: 10, zIndex: 100000,
                  background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
                  borderRadius: '50%', width: 54, height: 54, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)', transition: 'all 0.2s ease',
                  backdropFilter: 'blur(4px)'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.8)'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.transform = 'scale(1)'; }}
                title="Next Product (→)"
              >
                <ChevronRight size={34} />
              </button>
            )}
          </div>

          <div style={{ marginTop: 14, color: '#94a3b8', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span>Use <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 7px', borderRadius: 4, color: '#fff', fontFamily: 'monospace' }}>←</kbd> and <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 7px', borderRadius: 4, color: '#fff', fontFamily: 'monospace' }}>→</kbd> arrow keys to change product</span>
            <span>&bull;</span>
            <span>Click anywhere on background to close</span>
          </div>
        </div>
      )}

      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}



// â”€â”€â”€ Users Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
import { usersAPI, masterAPI as mAPI } from '../services/api';
import { Settings } from 'lucide-react';

const ALL_PERMISSIONS = [
  'Dashboard', 'Sales', 'Purchase', 'Inventory',
  'Customers', 'Suppliers', 'Items', 'Branches',
  'Reports', 'GST', 'Users'
];

export function UsersPage() {
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editID, setEditID] = useState(null);
  const [form, setForm] = useState({ fullName: '', username: '', password: '', email: '', mobile: '', roleID: '', branchID: '', permissions: ALL_PERMISSIONS.join(',') });

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, b] = await Promise.all([usersAPI.getAll(), mAPI.getRoles(), mAPI.getBranches()]);
      setData(u.data.data || []); setRoles(r.data.data || []); setBranches(b.data.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editID) {
        await usersAPI.update(editID, {
          fullName: form.fullName,
          email: form.email,
          mobile: form.mobile,
          roleID: +form.roleID,
          branchID: form.branchID ? +form.branchID : null,
          permissions: form.permissions
        });
        toast.success('User updated');
      } else {
        await usersAPI.create({ ...form, roleID: +form.roleID, branchID: form.branchID ? +form.branchID : null });
        toast.success('User created');
      }
      setModal(false); setEditID(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const handleToggle = async (id, name, active) => {
    if (!window.confirm(`${active ? 'Deactivate' : 'Activate'} user "${name}"?`)) return;
    try { await usersAPI.toggle(id); toast.success('Done'); load(); } catch { toast.error('Failed'); }
  };
  const openEdit = (row) => {
    setForm({
      fullName: row.fullName,
      username: row.username,
      password: '',
      email: row.email || '',
      mobile: row.mobile || '',
      roleID: String(row.roleID),
      branchID: row.branchID ? String(row.branchID) : '',
      permissions: row.permissions || ''
    });
    setEditID(row.userID);
    setModal(true);
  };
  const handlePermissionChange = (perm, checked) => {
    const list = form.permissions ? form.permissions.split(',').map(x => x.trim()).filter(Boolean) : [];
    let nextList;
    if (checked) {
      nextList = [...list, perm];
    } else {
      nextList = list.filter(x => x !== perm);
    }
    setForm({ ...form, permissions: nextList.join(',') });
  };

  const inp = (k) => ({ value: form[k], onChange: e => setForm({ ...form, [k]: e.target.value }) });

  const columns = [
    { header: 'Name', key: 'fullName', render: r => <span style={{ fontWeight: 600 }}>{r.fullName}</span> },
    { header: 'Username', key: 'username', render: r => <code style={{ fontSize: 12 }}>{r.username}</code> },
    { header: 'Role', key: 'roleName', render: r => <span className="badge badge-blue">{r.roleName}</span> },
    { header: 'Branch', key: 'branchName', render: r => r.branchName || 'All' },
    { header: 'Status', key: 'isActive', render: r => <span className={`badge ${r.isActive ? 'badge-green' : 'badge-red'}`}>{r.isActive ? 'Active' : 'Inactive'}</span> },
    { header: 'Last Login', key: 'lastLogin', render: r => r.lastLogin ? new Date(r.lastLogin).toLocaleDateString('en-IN') : <span className="text-muted">Never</span> },
    {
      header: 'Actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit User" onClick={() => openEdit(r)}>
            <Edit2 size={14} />
          </button>
          <button className="btn btn-ghost btn-sm" style={{ color: r.isActive ? 'var(--red)' : 'var(--green)' }} onClick={() => handleToggle(r.userID, r.fullName, r.isActive)}>
            {r.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="flex-between mb-6">
        <div><h1>User Management</h1><p className="text-muted">{data.length} users</p></div>
        <button className="btn btn-amber" onClick={() => { setForm({ fullName: '', username: '', password: '', email: '', mobile: '', roleID: '', branchID: '', permissions: ALL_PERMISSIONS.join(',') }); setEditID(null); setModal(true); }}><Plus size={16} /> Add User</button>
      </div>
      <div className="card">
        <div style={{ padding: '16px 20px' }}>
          <DataTable data={data} columns={columns} loading={loading} searchKeys={['fullName', 'username', 'roleName']} emptyIcon={<Building2 size={36} />} emptyText="No users found" />
        </div>
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && (setModal(false), setEditID(null))}>
          <div className="modal">
            <div className="modal-header"><h3>{editID ? 'Edit User' : 'Add User'}</h3><button className="btn btn-ghost btn-icon" onClick={() => { setModal(false); setEditID(null); }}><X size={18} /></button></div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="grid-2" style={{ gap: 12 }}>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Full Name *</label><input className="form-control" required {...inp('fullName')} /></div>
                  {!editID && (
                    <>
                      <div className="form-group"><label className="form-label">Username *</label><input className="form-control" required {...inp('username')} /></div>
                      <div className="form-group"><label className="form-label">Password *</label><input className="form-control" type="password" required {...inp('password')} /></div>
                    </>
                  )}
                  <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" {...inp('email')} /></div>
                  <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" {...inp('mobile')} /></div>
                  <div className="form-group"><label className="form-label">Role *</label>
                    <SearchableSelect
                      value={form.roleID}
                      onChange={val => setForm(prev => ({ ...prev, roleID: val }))}
                      options={roles.map(r => ({ value: r.roleID, label: r.roleName }))}
                      placeholder="Select role"
                      required
                    />
                  </div>
                  <div className="form-group"><label className="form-label">Branch</label>
                    <SearchableSelect
                      value={form.branchID}
                      onChange={val => setForm(prev => ({ ...prev, branchID: val }))}
                      options={branches.map(b => ({ value: b.branchID, label: b.branchName }))}
                      placeholder="All Branches"
                    />
                  </div>
                  <div style={{ gridColumn: '1/-1', marginTop: 8 }}>
                    <label className="form-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Permissions</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, background: 'var(--silver-100)', padding: 12, borderRadius: 8, border: '1px solid var(--silver-200)' }}>
                      {ALL_PERMISSIONS.map(p => {
                        const checked = (form.permissions || '').split(',').map(x => x.trim()).includes(p);
                        return (
                          <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => handlePermissionChange(p, e.target.checked)}
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            <span>{p}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => { setModal(false); setEditID(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />}{editID ? 'Update User' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bank / Company Settings Page ────────────────────────────────────────────
const EMPTY_SETTINGS = {
  bankName: '', accountHolder: '', accountNo: '', ifscCode: '', upiid: '', bankBranch: '', terms: ''
};

export function BankSettingsPage() {
  const [form, setForm] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrPreview, setQrPreview] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.get();
        const d = res.data.data;
        if (d) {
          setForm({
            bankName: d.bankname || '',
            accountHolder: d.accountholder || '',
            accountNo: d.accountno || '',
            ifscCode: d.ifsccode || '',
            upiid: d.upiid || '',
            bankBranch: d.bankbranch || '',
            terms: d.terms || '',
          });
        }
      } catch { toast.error('Failed to load settings'); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (form.upiid) {
      const upiStr = `upi://pay?pa=${encodeURIComponent(form.upiid)}&pn=${encodeURIComponent(form.accountHolder || 'Carat Quest')}&cu=INR`;
      QRCode.toDataURL(upiStr, { width: 160, margin: 1 })
        .then(url => setQrPreview(url))
        .catch(() => setQrPreview(`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(upiStr)}`));
    } else {
      setQrPreview('');
    }
  }, [form.upiid, form.accountHolder]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.save({
        bankName: form.bankName,
        accountHolder: form.accountHolder,
        accountNo: form.accountNo,
        ifscCode: form.ifscCode,
        upiID: form.upiid,
        bankBranch: form.bankBranch,
        terms: form.terms,
      });
      toast.success('Settings saved successfully!');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const field = (key) => ({ value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12, color: 'var(--text-muted)' }}><span className="spinner" /> Loading settings...</div>;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
        borderRadius: 16,
        padding: '24px 28px',
        color: '#fff',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 10px 25px -5px rgba(30, 58, 95, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Building2 size={26} color="#c5a059" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>Bank & Invoice Settings</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Manage bank account details, UPI payment QR code, and invoice footer terms</p>
          </div>
        </div>
      </div>

      <form onSubmit={save}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid var(--border)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          marginBottom: 24,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 24px',
            background: 'linear-gradient(to right, #fafafa, #fff)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(30,58,95,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CreditCard size={17} color="#1e3a5f" />
              </div>
              <span style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text)' }}>Bank Account Details</span>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-subtle)', padding: '4px 10px', borderRadius: 20, fontWeight: 500 }}>
              Printed on sales invoices
            </span>
          </div>

          <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px 24px' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>Bank Name</label>
              <input className="form-input" placeholder="e.g. Bank of Baroda" style={{ borderRadius: 8, padding: '10px 14px' }} {...field('bankName')} />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>Account Holder Name</label>
              <input className="form-input" placeholder="e.g. SHIVORA INTERNATIONAL LLP" style={{ borderRadius: 8, padding: '10px 14px' }} {...field('accountHolder')} />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>Account Number</label>
              <input className="form-input" placeholder="e.g. 12345678910" style={{ borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace' }} {...field('accountNo')} />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>IFSC Code</label>
              <input className="form-input" placeholder="e.g. BOB01231" style={{ borderRadius: 8, padding: '10px 14px', textTransform: 'uppercase', fontFamily: 'monospace' }} {...field('ifscCode')} />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>Bank Branch</label>
              <input className="form-input" placeholder="e.g. VARACHHA, SURAT" style={{ borderRadius: 8, padding: '10px 14px' }} {...field('bankBranch')} />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>
                UPI ID <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.72rem' }}>(Generates auto QR code)</span>
              </label>
              <input className="form-input" placeholder="e.g. hello@12345 or 9819955577@axisbank" style={{ borderRadius: 8, padding: '10px 14px', border: form.upiid ? '1px solid #10b981' : '1px solid var(--border)' }} {...field('upiid')} />
            </div>
          </div>

          {form.upiid && (
            <div style={{ margin: '0 24px 24px', padding: '18px 20px', background: 'linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(16,185,129,0.1) 100%)', borderRadius: 14, border: '1.5px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', gap: 20 }}>
              {qrPreview ? (
                <img src={qrPreview} width="108" height="108" alt="Live QR Preview" style={{ border: '2px solid #10b981', borderRadius: 12, padding: 5, background: '#fff', boxShadow: '0 6px 16px rgba(16,185,129,0.2)', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 108, height: 108, background: '#fff', borderRadius: 12, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>Generating...</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#047857', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>✅ Live Payment QR Code Generated</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, background: 'rgba(255,255,255,0.7)', padding: '6px 12px', borderRadius: 8, width: 'fit-content', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#5f259f" /><text x="12" y="16.5" fill="#fff" fontSize="14" fontWeight="900" textAnchor="middle" fontFamily="Arial, sans-serif">पे</text></svg>
                    <span style={{ color: '#5f259f', fontWeight: 700, fontSize: '10px' }}>PhonePe</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335" /></svg>
                    <span style={{ color: '#5f6368', fontWeight: 600, fontSize: '10px' }}>Pay</span>
                  </span>
                  <span style={{ fontWeight: 900, fontSize: '11px' }}>
                    <span style={{ color: '#002e6e' }}>pay</span><span style={{ color: '#00baf2' }}>tm</span>
                  </span>
                  <span style={{ fontStyle: 'italic', fontWeight: 900, fontSize: '11px', color: '#555', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>UPI</span>
                    <svg width="14" height="11" viewBox="0 0 16 14" fill="none"><path d="M2 14L8 7L2 0H6.5L12.5 7L6.5 14H2Z" fill="#008822" /><path d="M7.5 14L13.5 7L7.5 0H10L16 7L10 14H7.5Z" fill="#FF9900" /></svg>
                  </span>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text)', marginBottom: 6 }}>
                  UPI ID: <strong style={{ color: '#047857', fontFamily: 'monospace' }}>{form.upiid}</strong> &nbsp;•&nbsp; Payee Name: <strong>{form.accountHolder || 'Carat Quest'}</strong>
                </div>
                <div style={{ fontSize: '0.74rem', color: '#047857', background: 'rgba(16,185,129,0.12)', padding: '4px 10px', borderRadius: 6, fontFamily: 'monospace', wordBreak: 'break-all', display: 'inline-block' }}>
                  upi://pay?pa={form.upiid}&pn={form.accountHolder || 'Carat Quest'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid var(--border)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          marginBottom: 28,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 24px',
            background: 'linear-gradient(to right, #fafafa, #fff)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(30,58,95,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Save size={17} color="#1e3a5f" />
              </div>
              <span style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text)' }}>Terms & Conditions</span>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-subtle)', padding: '4px 10px', borderRadius: 20, fontWeight: 500 }}>
              Only printed on invoice if filled
            </span>
          </div>

          <div style={{ padding: '24px' }}>
            <textarea
              className="form-input"
              rows={4}
              placeholder={"1. Goods once sold will not be taken back or exchanged.\n2. This is a computer generated invoice, no signature required.\n3. Subject to SURAT jurisdiction."}
              style={{
                resize: 'vertical',
                fontFamily: 'inherit',
                borderRadius: 10,
                padding: '12px 14px',
                lineHeight: 1.5,
                fontSize: '0.85rem'
              }}
              {...field('terms')}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 32px',
              fontSize: '0.92rem',
              fontWeight: 700,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 6px 18px rgba(30, 58, 95, 0.25)',
              transition: 'all 0.2s ease'
            }}
          >
            {saving ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Saving Settings...</> : <><Save size={18} color="#c5a059" /> Save Settings</>}
          </button>
        </div>
      </form>
    </div>
  );
}
